/** Datenzugriff über TanStack Query -- Cache, Wiederholung und Invalidierung. */

import { showsPicture, type DayCardDisplay } from '../lib/dayCardDisplay'
import { compareMessages } from '../lib/chatOrder'
import { insertionFields, type Insertion } from '../lib/insertion'
import type { Vehicle } from '../lib/vehicles'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { nachrichtRumpf, type NachrichtTeile } from '../api/nachrichtRumpf'
import { ApiError, api, zeitgrenze } from '../api/client'
import { vergissOrt } from '../lib/letzterChat'
import { maintenanceOver } from '../lib/maintenance'
import { vorratLeeren } from '../lib/vorrat'
import { versatzDesGeraets } from '../lib/ortszeit'
import { zoneDesGeraets, type Zoneneintrag } from '../lib/zonen'
import { aufnahmeKopf, vorschauAus } from '../lib/images'
import { vorschauHochladen } from '../lib/vorschau'
import type { Login, Registration } from '../lib/passkey'
import type { Aufnahme } from '../lib/exif'
import type {
  Galerieseite,
  Tagesliste,
  Routenergebnis,
  SearchPage,
  Candidate,
  DatenbankErgebnis,
  DiagnosisResult,
  MaintenanceState,
  DatenbankVorschau,
  EnvSeite,
  Verwaltungsprotokoll,
  Selbstpruefung,
  Health,
  Highlight,
  HighlightsResponse,
  Invitation,
  Loeschbefund,
  Loeschvorschau,
  LocateResponse,
  LookupResponse,
  MapStyle,
  PublishManyResponse,
  Reisestatistik,
  SnapResponse,
  InvitationPreview,
  InvitationStatus,
  JoinRequest,
  Me,
  Dateiabgleich,
  Media,
  Medienpruefung,
  Member,
  Message,
  Person,
  MessagePage,
  PlaylistItem,
  Poll,
  PollsResponse,
  Reaction,
  Reisefotos,
  Reisefotoziel,
  RescanResponse,
  Settings,
  SettingsResponse,
  Speicher,
  Thread,
  Track,
  TracksResponse,
  TrackSectionsAnswer,
  Trip,
  TripDeleteBefund,
  TripDeleteVorschau,
  VorschlaegeZahl,
  RegionsResponse,
  TripResetBefund,
  TripResetVorschau,
  TripState,
  UmgebungResponse,
  Passkey,
  DayCardFilm,
  DayCardPreview,
  DaySeparator,
  TagestrennerAngaben,
  Tagestrennerliste,
} from '../api/types'

export const keys = {
  me: ['me'] as const,
  threads: ['threads'] as const,
  people: ['people'] as const,
  trips: ['trips'] as const,
  trip: (id: number) => ['trip', id] as const,
  members: (id: number) => ['members', id] as const,
  invitations: (id: number) => ['invitations', id] as const,
  requests: (id: number) => ['requests', id] as const,
  candidates: (id: number) => ['candidates', id] as const,
  invitationStatus: (code: string) => ['invitation-status', code] as const,
  settings: ['settings'] as const,
  passkeys: ['passkeys'] as const,
  messages: (tripId: number, withUser?: number) =>
    ['messages', tripId, withUser ?? null] as const,
  tracks: (tripId: number) => ['tracks', tripId] as const,
  highlights: (tripId: number) => ['highlights', tripId] as const,
  polls: (tripId: number) => ['polls', tripId] as const,
  // With a target as a third part (1.53.3): the list differs by where the
  // pictures go. The prefix without it still invalidates every variant.
  reisefotos: (tripId: number, ziel?: Reisefotoziel) =>
    ziel === undefined ? (['reisefotos', tripId] as const) : (['reisefotos', tripId, ziel] as const),
  suche: (tripId: number, q: string) => ['suche', tripId, q] as const,
  galerie: (tripId: number) => ['galerie', tripId] as const,
  /** The draft chat (#85): the traveller's own, no partner ever. */
  entwurf: (tripId: number) => ['entwurf', tripId] as const,
  /** Die Playlist einer Reise (#102, 1.58.0). */
  playlist: (tripId: number) => ['playlist', tripId] as const,
  tage: (tripId: number, withUser: number | undefined, tz: number) => ['tage', tripId, withUser ?? null, tz] as const,
  /** The day separators of a trip (#113): the list for the pill and the proposal for the next. */
  tagestrenner: (tripId: number) => ['tagestrenner', tripId] as const,
  stats: (tripId: number) => ['stats', tripId] as const,
  speicher: ['speicher'] as const,
  abgleich: ['abgleich'] as const,
  /*
   * **Die neun, die daneben ausgeschrieben standen** (1.23.0).
   *
   * Sie standen als Literal neben dieser Liste -- `['operations', 'env']` an
   * drei Stellen, `['admin', 'log']` an zwei -- und eine Liste, die nicht
   * jeden Schlüssel enthält, fragt niemand. Die Reihen unten sind die alten,
   * Zeichen für Zeichen: ein umbenannter Schlüssel ist ein zweiter
   * Zwischenspeicher neben dem ersten, und die Seite läse einen Eintrag, der
   * sich nie füllt.
   */
  health: ['health'] as const,
  invitation: (code: string) => ['invitation', code] as const,
  resetPreview: (tripId: number) => ['reset-vorschau', tripId] as const,
  deletePreview: (tripId: number) => ['delete-vorschau', tripId] as const,
  vorschlaege: (tripId: number) => ['vorschlaege', tripId] as const,
  regionen: ['regionen'] as const,
  accountDeletePreview: (id: number) => ['loeschvorschau', id] as const,
  envCatalogue: ['operations', 'env'] as const,
  adminLog: ['admin', 'log'] as const,
  selfCheck: ['operations', 'check'] as const,
  databaseCleanup: ['operations', 'cleanup'] as const,
  maintenance: ['operations', 'maintenance'] as const,
  mediaCheck: ['medienpruefung'] as const,
}

/**
 * Wer hier ist -- und was er darf.
 *
 * **`refetchOnMount: 'always'` seit dem Vorrat (#13), und das ist kein
 * Feinschliff.** Seit der Zwischenspeicher das Schließen überlebt, kommt diese
 * Antwort beim Start aus der IndexedDB -- mit ihrem alten Alter. Innerhalb der
 * fünf Minuten Haltbarkeit hielt react-query sie damit für frisch und fragte
 * gar nicht nach. Im Prüflauf war das sofort zu sehen: ein gerade
 * freigeschaltetes Konto bekam weiter „Noch nichts freigegeben" zu lesen, und
 * ein Neuladen half nicht. Vorher konnte das nicht passieren, weil ein
 * Neuladen den Zwischenspeicher ohnehin leerte.
 *
 * Der gemerkte Stand wird trotzdem **sofort** gezeigt -- deshalb steht hier
 * `'always'` und nicht ein kürzeres `staleTime`: kein Ladebalken, keine
 * Weiterleitung zur Anmeldung, und die frische Auskunft kommt gleich
 * hinterher. Ohne Netz scheitert sie, und der gemerkte Stand bleibt stehen.
 * Genau so soll es sein.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<Me>('/api/auth/me'),
    // Eine fehlende Anmeldung ist kein Fehler, der sich durch Wiederholen
    // beheben ließe.
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnMount: 'always',
  })
}

/**
 * Der Zustand der Installation -- nur gefragt, wenn etwas schiefgeht.
 *
 * Ausgeliefert wird per FTP, und das Schema zieht ein Mensch von Hand nach.
 * Bleibt dabei etwas liegen, ist die eigentliche Auskunft „es fehlt die
 * Spalte X" -- und die gehört vor die Nase dessen, der davorsteht, nicht in
 * ein Serverprotokoll.
 */
export function useHealth(enabled: boolean) {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => api.get<Health>('/api/health'),
    enabled,
    retry: false,
  })
}

/**
 * The same answer, asked every half minute -- for the maintenance page
 * (#131), which waits for the server to let this device through again.
 * React Query pauses the interval in the background and asks again when
 * the app comes to the front.
 */
export function useHealthWatch() {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => api.get<Health>('/api/health'),
    retry: false,
    staleTime: 0,
    refetchInterval: 30_000,
  })
}

/**
 * Die einstellbaren Zahlen.
 *
 * Braucht jeder, nicht nur die Verwaltung: die Bildgröße wirkt im Browser,
 * und der verkleinert, bevor irgendetwas zum Server geht. Deshalb lange
 * haltbar und mit Vorgaben unterlegt -- ein Foto darf nicht darauf warten
 * müssen, dass diese Abfrage durch ist.
 */
export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => api.get<SettingsResponse>('/api/settings'),
    staleTime: 10 * 60_000,
  })
}

/**
 * The bounds of the day film's length, from the server (1.121.0); `null`
 * until the settings are there. Nobody keeps a copy: the field, the sheet and
 * the script take them from here.
 */
export function useFilmLength(): { min: number; max: number } | null {
  return useSettings().data?.film_length ?? null
}

/** The bounds of „Nach Strecke“ in tenths, from the server (1.123.0); `null` until the settings are there. */
export function useFilmRate(): { min: number; max: number } | null {
  return useSettings().data?.film_rate ?? null
}

/**
 * Die Werte -- und zwar wirklich, nicht "vielleicht schon da".
 *
 * Für das Verkleinern eines Fotos reicht `useSettings()` nicht: wer ein Bild
 * auswählt, während die Abfrage noch unterwegs ist, bekäme die Vorgabe statt
 * der eingestellten Größe. Genau das ist im Test passiert -- und es wäre der
 * unangenehme Fall, weil das Foto dann *aussieht* wie eines, das der
 * Einstellung folgt.
 *
 * Läuft die Abfrage schon oder ist sie frisch, kostet das nichts.
 */
export async function ensureSettings(client: QueryClient): Promise<Settings | null> {
  try {
    const antwort = await client.ensureQueryData({
      queryKey: keys.settings,
      queryFn: () => api.get<SettingsResponse>('/api/settings'),
      staleTime: 10 * 60_000,
    })
    return antwort.settings
  } catch {
    // Ohne Antwort gelten die Vorgaben -- ein Foto soll nicht daran
    // scheitern, dass eine Nebensache nicht durchkam.
    return null
  }
}

export function useSaveSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (werte: Partial<Settings>) =>
      api.patch<SettingsResponse>('/api/settings', werte),
    onSuccess: (antwort) => {
      // Die Antwort trägt den geltenden Stand -- also gekappt, falls jemand
      // über die Decke der `.env` hinaus wollte. Sie zu setzen statt neu zu
      // fragen zeigt genau das an, ohne einen zweiten Weg zum Server.
      client.setQueryData(keys.settings, antwort)
    },
  })
}

export function useThreads() {
  return useQuery({
    queryKey: keys.threads,
    queryFn: () => api.get<Thread[]>('/api/threads'),
    // Bis der Live-Stream aus Etappe 2 da ist, hält ein ruhiges Nachfragen
    // die Chatliste aktuell.
    refetchInterval: 20_000,
  })
}

/**
 * Die Route einer Reise.
 *
 * Lange haltbar und ohne Takt: eine Aufzeichnung kommt schubweise, nicht im
 * Minutentakt -- und sie ist um Größenordnungen schwerer als eine Seite
 * Nachrichten. Wer sie neu will, öffnet die Karte neu.
 */
export function useTracks(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.tracks(tripId),
    queryFn: () => api.get<TracksResponse>(`/api/trips/${tripId}/tracks`),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useUploadTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ file, vehicle, local }: { file: File; vehicle: Vehicle; local: boolean }) =>
      api.upload<Track>(`/api/trips/${tripId}/tracks`, file, undefined, {
        // No zone (1.87.0): a GPX file is UTC, and the server takes each
        // stop's offset from the trip's zone course at that moment. Unless
        // the app that wrote it put the wall clock there (1.117.0): then the
        // server rewrites the file once, in the same course (`TrackTimes`).
        'x-clock': local ? 'local' : 'utc',
        'x-datei-name': file.name,
        // The vehicle of the recording (#90), beside the file like its name.
        'x-vehicle': vehicle,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
      // **Auch die Highlights.** Der Import liest Aufenthalte aus der
      // Aufzeichnung und legt sie als Vorschläge an -- ohne diese Zeile stünde
      // in der Leiste „1 Vorschlag", und auf der Karte wäre keiner. Genau so
      // im Prüflauf aufgefallen.
      void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    },
  })
}

/**
 * Connect highlights with a calculated route (1.18.0, #84). The server asks
 * Apple once per leg and stores the answer like an imported recording; the
 * list of tracks is therefore what changes.
 */
export function useVerbinden(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { highlight_ids: number[]; transport_type?: string; avoid_tolls?: boolean }) =>
      api.post<Routenergebnis>(`/api/trips/${tripId}/routes`, input, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
    },
  })
}

/**
 * Eine vorhandene Aufzeichnung noch einmal auf Aufenthalte ansehen.
 *
 * **Eine Datei je Aufruf**, und die Oberfläche geht die Liste durch. Neunzehn
 * Dateien in einer Anfrage wären auf einem geteilten Paket der Bau, der nach
 * der halben Route in die Laufzeitgrenze läuft.
 */
export function useRescan(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (trackId: number) => api.post<RescanResponse>(`/api/tracks/${trackId}/rescan`, {}, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    onSuccess: (antwort) => {
      // Nur wenn etwas entstanden ist -- sonst holt ein Durchgang über zwanzig
      // Dateien zwanzigmal dieselbe Liste.
      if (antwort.suggested > 0) {
        void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      }
    },
  })
}

/**
 * The sections of a recording and their vehicles (#90, 1.96.0): every
 * change answers with the whole recording, which takes its place in the
 * list. The day card's preview reads the sections anew when it is opened.
 */
export type TrackSectionChange =
  | { kind: 'first'; trackId: number; vehicle?: Vehicle; speed?: number }
  | { kind: 'split'; trackId: number; splits: { at: string; vehicle?: Vehicle }[] }
  | { kind: 'vehicle'; trackId: number; splitId: number; vehicle?: Vehicle; speed?: number }
  | { kind: 'merge'; trackId: number; splitId: number }
  | { kind: 'clearAll'; trackId: number }

/**
 * Changes of a vehicle or a speed still under way, per recording (1.98.1).
 * Only the answer to the last one is written into the list: an earlier
 * answer arriving after a later tap would put the earlier choice back for
 * a moment.
 */
const sectionChangesUnderWay = new Map<number, number>()

/**
 * The requests for one recording, one after the other (1.98.1). Three quick
 * taps are three requests, and the server may take them in another order
 * than they were sent (several PHP workers, a slow line) -- the vehicle
 * saved would then be the second-to-last. The sheet shows each tap at once
 * anyway (`onMutate`); only the requests wait for each other.
 */
const sectionQueue = new Map<number, Promise<unknown>>()
function inTurn<T>(trackId: number, request: () => Promise<T>): Promise<T> {
  const before = sectionQueue.get(trackId) ?? Promise.resolve()
  const next = before.catch(() => undefined).then(request)
  sectionQueue.set(trackId, next)
  return next
}

export function useTrackSections(tripId: number) {
  const client = useQueryClient()
  // The answer carries the sections and not the points (1.100.0): it is set
  // into the recording the list has, which keeps its points and its flight
  // proposals -- neither changes with a section.
  const replace = (answer: TrackSectionsAnswer) => {
    client.setQueryData<TracksResponse>(keys.tracks(tripId), (old) =>
      old === undefined
        ? old
        : { ...old, tracks: old.tracks.map((t) => (t.id === answer.id ? { ...t, vehicle: answer.vehicle, sections: answer.sections } : t)) },
    )
  }
  return useMutation({
    // **The choice stands at once** (1.98.1). From use: "das Wechseln des
    // Fahrzeugsymbols dauert lange, anfangs muss man auch öfter drücken".
    // Until then every button waited for the server and was locked
    // meanwhile, so a tap in that time did nothing. A vehicle or a speed is
    // written into the list before the request; splitting and merging still
    // wait, because only the server knows where the sections then run.
    onMutate: (change: TrackSectionChange) => {
      if (change.kind !== 'first' && change.kind !== 'vehicle') return
      sectionChangesUnderWay.set(change.trackId, (sectionChangesUnderWay.get(change.trackId) ?? 0) + 1)
      const splitId = change.kind === 'first' ? null : change.splitId
      const part = {
        ...(change.vehicle === undefined ? {} : { vehicle: change.vehicle }),
        ...(change.speed === undefined ? {} : { speed: change.speed }),
      }
      client.setQueryData<TracksResponse>(keys.tracks(tripId), (old) =>
        old === undefined
          ? old
          : {
              ...old,
              tracks: old.tracks.map((t) =>
                t.id !== change.trackId
                  ? t
                  : {
                      ...t,
                      ...(splitId === null && change.vehicle !== undefined ? { vehicle: change.vehicle } : {}),
                      sections: t.sections?.map((s) => (s.split_id === splitId ? { ...s, ...part } : s)),
                    },
              ),
            },
      )
    },
    onSettled: (_track, _error, change) => {
      if (change.kind !== 'first' && change.kind !== 'vehicle') return
      sectionChangesUnderWay.set(change.trackId, Math.max(0, (sectionChangesUnderWay.get(change.trackId) ?? 1) - 1))
    },
    // A refusal puts back what the server has (no silent refusal: the
    // sheet shows the error as well).
    onError: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
    },
    mutationFn: (change: TrackSectionChange) => inTurn(change.trackId, () => {
      switch (change.kind) {
        case 'first':
          return api.patch<TrackSectionsAnswer>(`/api/tracks/${change.trackId}`, { vehicle: change.vehicle, speed: change.speed })
        case 'split':
          return api.post<TrackSectionsAnswer>(`/api/tracks/${change.trackId}/splits`, { splits: change.splits })
        case 'vehicle':
          return api.patch<TrackSectionsAnswer>(`/api/tracks/${change.trackId}/splits/${change.splitId}`, { vehicle: change.vehicle, speed: change.speed })
        case 'merge':
          return api.delete<TrackSectionsAnswer>(`/api/tracks/${change.trackId}/splits/${change.splitId}`)
        case 'clearAll':
          return api.delete<TrackSectionsAnswer>(`/api/tracks/${change.trackId}/splits`)
      }
    }),
    onSuccess: (track, change) => {
      if ((change.kind === 'first' || change.kind === 'vehicle') && (sectionChangesUnderWay.get(change.trackId) ?? 0) > 1) return
      replace(track)
    },
  })
}

export function useDeleteTrack(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/tracks/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tracks(tripId) })
    },
  })
}

/**
 * Die Highlights einer Reise.
 *
 * Was hier ankommt, hat der Server schon gefiltert: ein Mitlesender bekommt
 * nur Freigegebenes und nur bis zu seinem Stichtag. Die Oberfläche sortiert
 * nicht nach und rechnet nichts nach — sie zeigt, was dasteht.
 */
/**
 * All polls of a trip, newest first (1.92.0, #116) -- the trip's page of
 * polls. The server decides which (`Visibility` by their post, deleted posts
 * excluded) and what each person sees of the result (`Abstimmung`).
 * `usePollAktion` invalidates the same key after every vote and close.
 */
export function usePolls(tripId: number) {
  return useQuery({
    queryKey: keys.polls(tripId),
    queryFn: () => api.get<PollsResponse>(`/api/trips/${tripId}/polls`),
    enabled: Number.isFinite(tripId) && tripId > 0,
    staleTime: 30_000,
  })
}

export function useHighlights(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.highlights(tripId),
    queryFn: () => api.get<HighlightsResponse>(`/api/trips/${tripId}/highlights`),
    enabled,
    staleTime: 60_000,
  })
}

/**
 * Anlegen, abstimmen, schließen.
 *
 * **Alle drei über einen Weg**, weil alle drei dasselbe brauchen: der Server
 * antwortet mit der fertigen Abstimmung, danach werden Liste **und** Verlauf
 * neu geholt. Der Verlauf muss mit: die Abstimmung steht dort als Kärtchen an
 * ihrem Beitrag, und eine Stimme, die nur in einer der beiden Ansichten
 * ankommt, ist der Fehler, den dieses Projekt „zwei Quellen für dieselbe
 * Frage" nennt.
 */
export function usePollAktion(tripId: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (
      aktion:
        | { art: 'anlegen'; question: string; options: string[]; multi: boolean; draft?: boolean; clientKey?: string; insertion?: Insertion | null }
        | { art: 'aendern'; pollId: number; question: string; options: { id: number; label: string }[] }
        | { art: 'abstimmen'; pollId: number; optionIds: number[] }
        | { art: 'schliessen'; pollId: number }
        // A poll closed by mistake, open again (1.126.0).
        | { art: 'oeffnen'; pollId: number },
    ): Promise<Poll | Message> => {
      // The question and the answers' words anew (1.86.0) -- every answer
      // by id, none added, none dropped: votes hang on them. The answer is
      // the whole post (1.86.1), set in below by id.
      if (aktion.art === 'aendern') {
        return api.patch<Message>(`/api/polls/${aktion.pollId}`, { question: aktion.question, options: aktion.options })
      }
      if (aktion.art === 'anlegen') {
        return api.post<Poll>(`/api/trips/${tripId}/polls`, {
          question: aktion.question,
          options: aktion.options,
          multi: aktion.multi,
          // Into the draft (#85) when asked from the draft page.
          draft: aktion.draft,
          // One key per poll, not per tap (1.75.0, `useSendKey`).
          client_key: aktion.clientKey,
          // The clock of the moment it was made (1.87.0), like a message's.
          tz_offset_min: versatzDesGeraets(),
          // Before or after a chosen post (#130).
          ...insertionFields(aktion.insertion),
        })
      }
      if (aktion.art === 'abstimmen') {
        return api.post<Poll>(`/api/polls/${aktion.pollId}/vote`, {
          option_ids: aktion.optionIds,
        })
      }
      if (aktion.art === 'oeffnen') {
        return api.post<Poll>(`/api/polls/${aktion.pollId}/open`, {})
      }
      return api.post<Poll>(`/api/polls/${aktion.pollId}/close`, {})
    },
    onSuccess: async (antwort) => {
      if (istNachricht(antwort)) nachrichtErsetzen(client, tripId, undefined, entwurf, antwort)
      else abstimmungEinsetzen(client, tripId, antwort)
      await client.invalidateQueries({ queryKey: keys.polls(tripId) })
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
    },
  })
}

/**
 * The poll as the server answers a vote or a close, set into every loaded
 * page of the trip at its post (1.117.0).
 *
 * From use, with a recording: the poll stood first with the sentence for
 * someone who has not voted, then with the percentages, and the box changed
 * its size. The vote reached the server, the device's store did not: the
 * fetch after it brings only the newest page fresh, and a vote marks nothing
 * for the tick (`ChatChanges`, on purpose: everyone would learn that someone
 * voted). A poll further up stayed "not voted" in the store and came back
 * like that on the next start. The answer is the whole poll, so it is set in
 * where it hangs -- the chat and the private chats, wherever it is loaded.
 */
function abstimmungEinsetzen(client: QueryClient, tripId: number, antwort: object): void {
  if (!('options' in antwort) || !('my_option_ids' in antwort)) return
  const poll = antwort as Poll
  client.setQueriesData<MessagePage>({ queryKey: ['messages', tripId] }, (old) => {
    if (old === undefined || !old.messages.some((m) => m.poll?.id === poll.id)) return old
    return { ...old, messages: old.messages.map((m) => (m.poll?.id === poll.id ? { ...m, poll } : m)) }
  })
}

/**
 * A post among the answers of a mutation that otherwise answers with a card
 * (the poll's vote and close answer with the poll, the day card's creation
 * with `{message_id}`). Asked by two fields only a post carries: `kind` and
 * the `author` object. The first draft asked for `author_id` -- the row's
 * column, not the JSON's key -- and the bench showed the old bubble.
 */
function istNachricht(antwort: object): antwort is Message {
  return 'kind' in antwort && 'author' in antwort
}

/**
 * The weather card (0.31.0): the device's position goes to the server, the
 * server asks Apple and posts the card to everyone. Afterwards the thread is
 * fetched anew, like after a poll -- the card is a message.
 */
/**
 * The day card (#79): the server says which day and hands over the points,
 * the browser draws the map (`zeichnen`), the server stores card and map.
 * Nothing is believed from the picture but the picture.
 */
export function useTageskarte(tripId: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (schritte: {
      /** The day the traveller chose (0.41.0) -- one of the days the server offered. */
      day: string
      /** And the part of it (0.42.0), as instants; the server checks both against the day. */
      from?: string
      to?: string
      /** Into the draft (#85) when asked from the draft page. */
      draft?: boolean
      /** One key per card, not per tap (1.75.0, `useSendKey`). */
      clientKey?: string
      /** Before or after a chosen post (#130). */
      insertion?: Insertion | null
      /** A card that is to be computed anew (1.86.0): the post it hangs on. */
      messageId?: number
      /** What it shows (#132): only numbers draw no map and make no film. */
      display: DayCardDisplay
      /** The line and the published points of the day (#80), as the server hands them over. */
      zeichnen: (punkte: [number, number][], nadeln: DayCardPreview['highlights']) => Promise<string | null>
      /**
       * The film instead of the map (#90): makes and uploads the video and
       * gives its id, or `null` when there is nothing to move -- then the
       * map is drawn as before. Throws on cancel, and nothing is sent.
       */
      film?: (vorschau: DayCardPreview) => Promise<number | null>
      /**
       * The heights for the complex film's profile (#139, 1.118.0): only on
       * the question for the preview, never in the body of the card.
       */
      elevation?: boolean
      /** The film settings the card stores (1.125.0), for "Neu erstellen". */
      stored?: DayCardFilm
      /**
       * The fingerprint of what the video would be made from (1.132.0,
       * `filmInput()`), asked once the day is read; `null` where it cannot
       * be had.
       */
      fingerprint?: (vorschau: DayCardPreview) => Promise<string | null>
      /**
       * The fingerprint the card's video was made from, when there is a
       * video to keep and "Video neu rechnen" is off. Equal to the new one,
       * the video stays: no film, no drawing, `keep_video` to the server.
       */
      keepIf?: string | null
      /** Told when the video was kept. */
      kept?: (vorschau: DayCardPreview) => void
      vorher: (stand: 'zeichnen' | 'schicken') => void
    }) => {
      const fenster = { day: schritte.day, from: schritte.from, to: schritte.to }
      const vorschau = await api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, schritte.elevation === true ? { ...fenster, elevation: '1' } : fenster)
      const picture = showsPicture(schritte.display)
      const input = picture && schritte.film !== undefined && schritte.fingerprint !== undefined ? await schritte.fingerprint(vorschau) : null
      const keep = input !== null && schritte.messageId !== undefined && (schritte.keepIf ?? null) === input
      const video = keep || schritte.film === undefined || !picture ? null : await schritte.film(vorschau)
      let rumpf: Record<string, unknown>
      // The fingerprint goes with a video only: a card that fell back to a
      // picture has no video to keep the next time.
      const film = schritte.stored === undefined ? {} : { film: (video !== null || keep) && input !== null ? { ...schritte.stored, input } : schritte.stored }
      if (keep) {
        schritte.kept?.(vorschau)
        schritte.vorher('schicken')
        rumpf = { ...fenster, day: vorschau.day, keep_video: true, display: schritte.display, ...film }
      } else if (!picture) {
        schritte.vorher('schicken')
        rumpf = { ...fenster, day: vorschau.day, display: schritte.display, ...film }
      } else if (video !== null) {
        schritte.vorher('schicken')
        rumpf = { ...fenster, day: vorschau.day, video_media_id: video, display: schritte.display, ...film }
      } else {
        schritte.vorher('zeichnen')
        const map = await schritte.zeichnen(vorschau.points, vorschau.highlights ?? [])
        // Its thumbnail with it (1.3.0): the map is a photo in the chat like
        // any other and gets the same little picture for the tile.
        const thumb = map === null ? null : await vorschauDataUrl(map, (await ensureSettings(client))?.thumb_max_edge)
        schritte.vorher('schicken')
        rumpf = { ...fenster, day: vorschau.day, map: map ?? '', thumb: thumb ?? '', display: schritte.display, ...film }
      }
      // The same drawing, into the same post (1.86.0): the server computes
      // anew and swaps the map; no key, no draft -- the post is there. The
      // answer is the whole post (1.86.1), set in below by id.
      if (schritte.messageId !== undefined) {
        return api.patch<Message>(`/api/trips/${tripId}/daycard/${schritte.messageId}`, rumpf)
      }
      return api.post<{ message_id: number }>(`/api/trips/${tripId}/daycard`, { ...rumpf, draft: schritte.draft, client_key: schritte.clientKey, tz_offset_min: versatzDesGeraets(), ...insertionFields(schritte.insertion) })
    },
    onSuccess: async (antwort) => {
      if (istNachricht(antwort)) nachrichtErsetzen(client, tripId, undefined, entwurf, antwort)
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** The thumbnail of a drawn JPEG data URL, as a data URL again -- or `null`. */
async function vorschauDataUrl(dataUrl: string, kante: number | undefined): Promise<string | null> {
  try {
    const blob = await (await fetch(dataUrl)).blob()
    const klein = await vorschauAus(blob, kante)
    if (klein === null) return null
    return await new Promise<string | null>((resolve) => {
      const leser = new FileReader()
      leser.onload = () => resolve(typeof leser.result === 'string' ? leser.result : null)
      leser.onerror = () => resolve(null)
      leser.readAsDataURL(klein)
    })
  } catch {
    return null
  }
}

export function useWetterkarte(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    // `at` (#83): a chosen hour in the past; without it the weather of now.
    // `draft` (#85): into the draft when asked from the draft page.
    // `tz_offset_min` (1.86.0): the zone the card shows its hour in.
    // `insertion` (#130): the same shape as for the poll and the day card.
    mutationFn: ({ insertion, ...where }: { lat: number; lon: number; tz_offset_min: number; at?: string; draft?: boolean; client_key?: string; insertion?: Insertion | null }) =>
      api.post<{ message_id: number }>(`/api/trips/${tripId}/weather`, { ...where, ...insertionFields(insertion) }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * The GIF of a post swapped for another (1.86.0): the chooser fetched the
 * new one (`POST …/media/gif`), the server hangs it in place of the old.
 * The post comes back whole and takes its place, like after an edit.
 */
export function useGifTauschen(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (eingabe: { messageId: number; mediaId: number }) =>
      api.patch<Message>(`/api/messages/${eingabe.messageId}/gif`, { media_id: eingabe.mediaId }),
    onSuccess: (message) => nachrichtErsetzen(client, tripId, withUser, entwurf, message),
  })
}

/**
 * A card about a chosen place and hour, changed (1.86.0): the same question
 * to Apple, the answer into the same card. The answer is the whole post
 * (1.86.1), set in by id -- fetching the list anew reached only the newest
 * page (`nachrichtErsetzen`).
 */
export function useWetterkarteAendern(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (eingabe: { messageId: number; lat: number; lon: number; at: string; tz_offset_min: number }) =>
      api.patch<Message>(`/api/trips/${tripId}/weather/${eingabe.messageId}`, {
        lat: eingabe.lat, lon: eingabe.lon, at: eingabe.at, tz_offset_min: eingabe.tz_offset_min,
      }),
    onSuccess: (message) => nachrichtErsetzen(client, tripId, withUser, entwurf, message),
  })
}

/**
 * Musik in den Chat (#102): der Link geht an den Server, der prüft ihn.
 *
 * **Ohne Ausgangskorb**, anders als bei der Wetterkarte: ob ein Link etwas
 * taugt, entscheidet der Server, und eine Karte, die stundenlang im Korb
 * liegt und dann abgewiesen wird, wäre schlechter als ein Satz im
 * Augenblick des Tippens.
 *
 * `meta.eigeneMeldung`: das Blatt zeigt die Absage selbst, unter dem Feld,
 * in dem der Link noch steht.
 */
/**
 * "Link zur Karte" (1.113.0): points already on the map, as a post with their
 * cards. Afterwards the chat, the draft, the list and the points are asked
 * again -- the last because the picker marks what stands in the chat.
 */
export function useKartenlink(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    meta: { eigeneMeldung: true },
    mutationFn: ({ insertion, ...eingabe }: { ids: number[]; draft?: boolean; client_key?: string; insertion?: Insertion | null }) =>
      api.post<{ message_id: number }>(`/api/trips/${tripId}/highlights/links`, { ...eingabe, tz_offset_min: versatzDesGeraets(), ...insertionFields(insertion) }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
      await client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    },
  })
}

export function useMusikkarte(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    meta: { eigeneMeldung: true },
    // `client_key` (1.75.0): one key per card, not per tap (`useSendKey`).
    mutationFn: ({ insertion, ...eingabe }: { url: string; body?: string; draft?: boolean; client_key?: string; insertion?: Insertion | null }) =>
      api.post<{ message_id: number }>(`/api/trips/${tripId}/music`, { ...eingabe, tz_offset_min: versatzDesGeraets(), ...insertionFields(insertion) }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.messages(tripId) })
      await client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
      await client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Die Playlist einer Reise (#102, 1.58.0): lesen, hinzufügen, herausnehmen.
 *
 * **Der Server antwortet jedes Mal mit der ganzen Liste**, und deshalb steht
 * hier kein Nachpflegen von Hand: Reihenfolge und Zahl kommen von dort, und
 * eine zweite Rechnung im Browser liefe auseinander.
 *
 * **Nur online**, wie die Musik im Chat: ob ein Link etwas taugt, entscheidet
 * der Server. Es gibt deshalb keinen Ausgangskorb und kein `client_key`.
 */
export function usePlaylist(tripId: number) {
  return useQuery({
    queryKey: keys.playlist(tripId),
    queryFn: () => api.get<{ items: PlaylistItem[]; may_edit: boolean }>(`/api/trips/${tripId}/playlist`),
    enabled: Number.isFinite(tripId),
  })
}

/** Ein Stück hinein -- `meta.eigeneMeldung`, denn das Blatt zeigt die Absage selbst. */
export function usePlaylistAnlegen(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    meta: { eigeneMeldung: true },
    mutationFn: (eingabe: { url: string; body?: string }) =>
      api.post<{ items: PlaylistItem[]; may_edit: boolean }>(`/api/trips/${tripId}/playlist`, eingabe),
    onSuccess: (antwort) => {
      client.setQueryData(keys.playlist(tripId), antwort)
    },
  })
}

/** Und wieder heraus. */
export function usePlaylistLoeschen(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (itemId: number) =>
      api.delete<{ items: PlaylistItem[]; may_edit: boolean }>(`/api/playlist/${itemId}`),
    onSuccess: (antwort) => {
      client.setQueryData(keys.playlist(tripId), antwort)
    },
  })
}

/**
 * Anlegen, ändern, wegnehmen, reagieren, freigeben.
 *
 * Alle über denselben Weg: der Server antwortet mit dem fertigen Highlight,
 * und danach wird die Liste neu geholt. Ein Punkt auf der Karte ist nichts,
 * was sich lohnt, im Zwischenspeicher von Hand nachzupflegen — dafür hängt zu
 * viel daran (Freigabe, Chatbeitrag, Reihenfolge).
 */
export function useHighlightMutations(tripId: number) {
  const client = useQueryClient()
  const frisch = () => {
    void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
    // Eine Freigabe schreibt einen Chatbeitrag -- der gehört sofort in den
    // Verlauf, sonst steht er erst nach dem nächsten Abholen da.
    void client.invalidateQueries({ queryKey: keys.messages(tripId) })
    void client.invalidateQueries({ queryKey: keys.threads })
  }

  const anlegen = useMutation({
    // Das Formular zeigt seine Absage selbst, als Absatz unter den Knoepfen
    // -- das globale Fangnetz haelt sich hier heraus (Codereview E2). Die
    // anderen Mutationen dieses Hooks (freigeben, zuruecknehmen, wegnehmen)
    // zeigen nichts und brauchen es: sie stehen im Blatt und in der Leiste.
    meta: { eigeneMeldung: true },
    mutationFn: (input: HighlightEingabe) =>
      api.post<Highlight>(`/api/trips/${tripId}/highlights`, input),
    onSuccess: frisch,
  })

  const aendern = useMutation({
    // Das Formular zeigt seine Absage selbst, als Absatz unter den Knoepfen
    // -- das globale Fangnetz haelt sich hier heraus (Codereview E2). Die
    // anderen Mutationen dieses Hooks (freigeben, zuruecknehmen, wegnehmen)
    // zeigen nichts und brauchen es: sie stehen im Blatt und in der Leiste.
    meta: { eigeneMeldung: true },
    mutationFn: (input: HighlightEingabe & { id: number }) => {
      const { id, ...rest } = input
      return api.patch<Highlight>(`/api/highlights/${id}`, rest)
    },
    onSuccess: frisch,
  })

  const wegnehmen = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/highlights/${id}`),
    onSuccess: frisch,
  })

  const reagieren = useMutation({
    mutationFn: (input: { id: number; emoji: string }) =>
      api.post<Highlight>(`/api/highlights/${input.id}/reactions`, { emoji: input.emoji }),
    onSuccess: frisch,
  })

  const freigeben = useMutation({
    mutationFn: (id: number) => api.post<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const zurueckziehen = useMutation({
    mutationFn: (id: number) => api.delete<Highlight>(`/api/highlights/${id}/publish`),
    onSuccess: frisch,
  })

  const alleFreigeben = useMutation({
    mutationFn: (until: string) =>
      api.post<PublishManyResponse>(`/api/trips/${tripId}/highlights/publish`, { until }),
    onSuccess: frisch,
  })

  return { anlegen, aendern, wegnehmen, reagieren, freigeben, zurueckziehen, alleFreigeben }
}

/** Was beim Anlegen und Ändern mitgeht -- alles freiwillig bis auf den Titel. */
export interface HighlightEingabe {
  lat?: number
  lon?: number
  title?: string
  body?: string
  category?: string
  happened_at?: string
  position_source?: Highlight['position_source']
  /** Die Schublade aus der Umgebungssuche — für das Zeichen vor dem Titel. */
  poi_category?: string
  time_source?: NonNullable<Highlight['time_source']>
  /** Die Uhr an diesem Punkt. Leer mitgeschickt heißt „gilt nicht mehr". */
  tz_offset_min?: number | ''
  show_in_chat?: boolean
  media_ids?: number[]
  /**
   * Was die Vorschau geholt hat — Ortsname, der rohe Block der Ortssuche, das
   * Wetter zur Stunde.
   *
   * Nur mitschicken, wenn wirklich frisch geholt: sie **überschreiben**, was am
   * Highlight steht, und der Server stempelt `enriched_at` dazu. Der Server
   * prüft die Form beim Annehmen; erfundene Felder fallen weg.
   */
  place_name?: string
  place?: Record<string, unknown> | null
  weather?: Record<string, unknown> | null
  /** Creating only: the form's key for this point (1.78.0, `useSendKey`). */
  client_key?: string
}

/**
 * Ort und Wetter nachholen — eines nach dem anderen.
 *
 * **Warum die Oberfläche das treibt und nicht der Server beim Import:** ein
 * Import mit fünfundzwanzig Aufenthalten wären fünfzig Abrufe nach außen in
 * einer einzigen Anfrage. Auf einem geteilten Paket mit begrenzter Laufzeit
 * ist das der Bau, der nach einem langen Reisetag abbricht und die halbe
 * Route eingespielt zurücklässt.
 *
 * Also hier, nacheinander, während die Kartenseite offen ist. Jedes Highlight
 * wird **einmal** geholt; der Server merkt sich das (`enriched_at`) auch dann,
 * wenn nichts zurückkam.
 */
export function useAngaben(tripId: number, highlights: Highlight[], erlaubt: boolean) {
  const client = useQueryClient()
  const laeuft = useRef(false)
  const [offen, setOffen] = useState(0)
  // A run that was cut short (the list changed underneath it) starts the
  // next one itself (1.22.0). Before, the cut-off run cleared `laeuft` and
  // nobody ran again until the count changed once more -- a point added
  // while the run was busy waited for the next reload. From the field:
  // "warum werden Ort/Wetter nicht automatisch geholt".
  const [neustart, setNeustart] = useState(0)

  const fehlend = highlights.filter((h) => h.enriched_at === null).length

  useEffect(() => {
    if (!erlaubt || laeuft.current) return
    const ids = highlights.filter((h) => h.enriched_at === null).map((h) => h.id)
    if (ids.length === 0) return

    laeuft.current = true
    setOffen(ids.length)
    let abgebrochen = false

    void (async () => {
      for (const id of ids) {
        if (abgebrochen) break
        try {
          await api.post(`/api/highlights/${id}/enrich`)
        } catch (fehler) {
          // Nicht eingerichtet oder kein Netz: dann bringt auch der nächste
          // nichts. Ein einzelner Fehlschlag am Dienst hält den Lauf dagegen
          // nicht auf — der Server merkt sich ihn und geht weiter.
          if (fehler instanceof ApiError && (fehler.offline || fehler.status === 409)) break
        }
        setOffen((n) => Math.max(0, n - 1))
      }

      laeuft.current = false
      setOffen(0)
      // Erst am Ende neu laden: mittendrin käme eine neue Liste herein und
      // der Lauf finge von vorn an.
      if (!abgebrochen) void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      else setNeustart((n) => n + 1)
    })()

    return () => {
      abgebrochen = true
    }
    // An der **Zahl** der offenen Einträge, nicht an der Liste: die ist bei
    // jedem Abruf ein neues Feld, und der Effekt liefe bei jedem Rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, erlaubt, fehlend, neustart])

  return offen
}

/**
 * Die zwei Vorschläge: Ort → Zeit und Zeit → Ort.
 *
 * Beide sind Vorschläge und nichts weiter. Die Route ist beim Import
 * ausgedünnt worden, und zwischen zwei behaltenen Punkten liegt eine gerade
 * Linie, auf der niemand war — deshalb kommt der Abstand mit heraus und die
 * Oberfläche lässt ablehnen.
 */
/**
 * Ort und Wetter zu einem Punkt ansehen, bevor etwas gespeichert ist.
 *
 * Der Weg über den Server ist der Punkt: nach außen geht so seine Adresse und
 * nicht die des Geräts. Gespeichert wird dabei nichts — was das Formular zeigt,
 * ist eine Auskunft, kein Eintrag. Erst das gesicherte Highlight holt sie sich
 * über `enrich` noch einmal und behält sie.
 */
export function useLookup(tripId: number) {
  return useMutation({
    mutationFn: (input: { lat: number; lon: number; at?: string }) =>
      api.post<LookupResponse>(`/api/trips/${tripId}/lookup`, input),
  })
}

/**
 * Was rund um den Punkt liegt — Lokale, Museen, Strände.
 *
 * **Geschickt wird eine Marke, kein Suchwort.** Aus `essen` macht der Server
 * „Restaurant" und eine Handvoll Apple-Kategorien (`server/src/Umgebung.php`);
 * hier steht davon nichts. Das ist nicht nur Ordnung: ein frei eingetipptes
 * Wort wäre die Suchanfrage eines Menschen bei einem fremden Dienst, und das
 * ist etwas anderes als eine von sieben Schubladen.
 *
 * Und wie bei `useLookup` geht die Anfrage über den eigenen Server — nach außen
 * geht seine Adresse, nicht die des Geräts.
 */
export function useUmgebung(tripId: number) {
  return useMutation({
    // `weit` wählt den zweiten, größeren Umkreis aus den Einstellungen --
    // für die Orte, die an einem Punkt als Zeile mitlaufen.
    // `kind` ist eine der acht Schubladen, `q` ein getipptes Wort — genau
    // eines von beidem. Mit Wort sucht der Server ohne Kategorien: Apple führt
    // keine für Pizzeria oder Imbiss, und ein Filter könnte den Treffer nur
    // wieder wegnehmen.
    mutationFn: (input: {
      kind?: string
      q?: string
      lat: number
      lon: number
      weit?: boolean
    }) => api.post<UmgebungResponse>(`/api/trips/${tripId}/umgebung`, input),
  })
}

export function useVorschlag(tripId: number) {
  const aufDieRoute = useMutation({
    mutationFn: (input: { lat: number; lon: number }) =>
      api.post<SnapResponse>(`/api/trips/${tripId}/snap`, input),
  })
  const zurZeit = useMutation({
    mutationFn: (at: string) => api.post<LocateResponse>(`/api/trips/${tripId}/locate`, { at }),
  })
  return { aufDieRoute, zurZeit }
}

/**
 * Die Reise — und zwar wirklich, nicht „vielleicht schon da".
 *
 * **Dieselbe Falle wie bei `ensureSettings()`, und sie ist zugeschnappt.**
 * Wer ein Foto auswählt, während die Abfrage noch unterwegs ist, gilt für
 * einen Augenblick als Mitlesender — und seit #59 hängt daran, mit welcher
 * Kantenlänge verkleinert wird. Der Prüfstand hat es gemessen: das Foto des
 * Autors kam mit 800 statt 2000 Punkten an, und es sieht dabei aus wie
 * eines, das der Einstellung folgt.
 *
 * Läuft die Abfrage schon oder ist sie frisch, kostet das nichts.
 */
export async function ensureTrip(client: QueryClient, tripId: number): Promise<Trip | null> {
  if (tripId <= 0) return null
  try {
    return await client.ensureQueryData({
      queryKey: keys.trip(tripId),
      queryFn: () => api.get<Trip>(`/api/trips/${tripId}`),
    })
  } catch {
    return null
  }
}

/**
 * The numbers of a trip (#78): route, posts, weather. The server decides what a
 * reader gets (`TripStats`); this hook only asks.
 */
/**
 * The numbers of a trip (#78). **With an end and without a retry** (1.53.2,
 * from use: "Statistik: es wird nichts mehr angezeigt"): the server read
 * every recording once per day and ran past its time limit; the browser
 * waited on a request without an end and then retried twice into the same
 * wall -- the card said "Zahlen werden geholt …" for minutes and then
 * nothing. Now the first visit after the update may take a while (the
 * server reads every recording once and keeps the numbers), but it ends,
 * and what ends with an error says so.
 */
export function useReisestatistik(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.stats(tripId),
    queryFn: () => api.get<Reisestatistik>(`/api/trips/${tripId}/stats`, undefined, zeitgrenze(STATS_TIMEOUT_MS)),
    enabled: enabled && tripId > 0,
    staleTime: 60_000,
    retry: false,
  })
}

export function useTrip(tripId: number) {
  return useQuery({
    queryKey: keys.trip(tripId),
    queryFn: () => api.get<Trip>(`/api/trips/${tripId}`),
    // **Keine Reise, keine Anfrage.** Seit die Hülle die offene Reise aus dem
    // Pfad ableitet (#49), wird dieser Haken auch mit 0 aufgerufen -- ohne
    // diese Zeile liefe auf jeder Seite außerhalb einer Reise eine Abfrage auf
    // `/api/trips/0` und endete in einem 404.
    enabled: tripId > 0,
  })
}

/**
 * Selbst aussteigen.
 *
 * Danach ist die Reise für dieses Konto weg — die Liste und alles, was an
 * ihr hängt, wird deshalb neu geholt und der Aufrufer schickt auf die
 * Startseite.
 */
export function useLeaveTrip(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(`/api/trips/${tripId}/members/me`),
    onSuccess: () => {
      // **Den gemerkten Ort mit vergessen** -- sonst schickt `StartPane`
      // einen beim nächsten Blick auf die Übersicht geradewegs in die Reise
      // zurück, aus der man gerade ausgestiegen ist. Dort steht dann „Diese
      // Reise gibt es nicht": genau die Sackgasse, gegen die es `vergissOrt`
      // gibt. Im Prüflauf aufgefallen, nicht am Schreibtisch.
      vergissOrt()
      // **Hier wirklich alles** (Codereview C11), und das ist die Ausnahme:
      // die Reise ist weg. Sie steht in der Liste, in der Chatliste, in ihrem
      // eigenen Verlauf, in Punkten, Aufzeichnungen und Fotos -- alles davon
      // zeigt gleich auf etwas, das es für dieses Konto nicht mehr gibt.
      void client.invalidateQueries()
    },
  })
}

/**
 * Den eigenen Namen ändern (#40).
 *
 * Danach steht er über allem, was dieses Konto geschrieben hat — deshalb wird
 * nicht nur `me` erneuert, sondern alles: Verläufe, Chatliste, Empfänger.
 * Ein Name, der an einer Stelle noch der alte ist, sieht aus wie ein zweiter
 * Mensch.
 */
export function useNameAendern() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (display_name: string) =>
      api.post<Me>('/api/auth/name', { display_name }),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      // Und hier ebenso, aus dem Grund im Doc-Block darüber: der Name steht
      // über allem, was dieses Konto je geschrieben hat.
      void client.invalidateQueries()
    },
  })
}

/**
 * Das eigene Passwort ändern (#45).
 *
 * **Der Zwischenspeicher bleibt, wie er ist.** Es ändert sich nichts an dem,
 * was dieses Konto sehen darf — nur die anderen Geräte sind danach abgemeldet,
 * und die erfahren es an ihrer nächsten Anfrage.
 */
export function usePasswortAendern() {
  return useMutation({
    mutationFn: (was: { current_password: string, new_password: string }) =>
      api.post<Me>('/api/auth/password', was),
  })
}

export function useMembers(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.members(tripId),
    queryFn: () => api.get<Member[]>(`/api/trips/${tripId}/members`),
    enabled,
  })
}

export function useInvitations(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitations(tripId),
    queryFn: () => api.get<Invitation[]>(`/api/trips/${tripId}/invitations`),
    enabled,
  })
}

export function useInvitationPreview(code: string) {
  return useQuery({
    queryKey: keys.invitation(code),
    queryFn: () => api.get<InvitationPreview>(`/api/invitations/${code}`),
    retry: false,
  })
}

/**
 * Wie viele Seiten eine Zusammenführung höchstens nachholt, um die Lücke zu
 * schließen.
 *
 * Fünf Seiten sind zweihundert Nachrichten seit dem letzten Takt -- wer so
 * lange weg war, hat keinen Chat offen gehabt, sondern die Anwendung neu
 * aufgebaut. Reicht es nicht, gilt der geladene Ausschnitt als verloren und
 * die Anzeige beginnt wieder oben; das ist der heutige Zustand und damit
 * kein Rückschritt. Ohne Grenze wäre es eine Schleife über den ganzen
 * Verlauf.
 */
const LUECKE_MAX_SEITEN = 5

/**
 * Die neueste Seite in den geladenen Ausschnitt einmischen (#42).
 *
 * **Was einmal geladen ist, bleibt geladen.** Der Ausschnitt reicht von der
 * neuesten Nachricht bis zur ältesten geladenen und wächst nur; ein Neuabruf
 * ist eine Zusammenführung und kein Ersatz.
 *
 * Innerhalb des Bereichs, den die frische Antwort abdeckt, **gilt sie**: was
 * dort fehlt, ist gelöscht und fliegt raus, was sich geändert hat (Haken,
 * Reaktionen, Bearbeitungen), kommt im neuen Stand. Darunter bleibt der
 * alte Stand unangetastet -- das ist der Teil, den der Knopf „Ältere
 * Nachrichten" geholt hat.
 *
 * `next_before` gehört der **ältesten geladenen** Nachricht, nicht der letzten
 * Antwort. Überlebt unten etwas, gilt der alte Merker weiter; sonst der der
 * letzten geholten Seite.
 *
 * `last_read_id` dagegen kommt aus der frischen Antwort -- wie bisher. Die
 * Trennlinie hängt ohnehin an dem Wert, den `ThreadPage` beim Aufbau
 * festhält; ein späterer Takt verschiebt sie nicht.
 */
export function seitenZusammenfuehren(
  alt: MessagePage | undefined,
  frisch: MessagePage,
): MessagePage {
  if (alt === undefined || alt.messages.length === 0) return frisch
  // **A changed order drops what lies below** (#130, 1.115.0). A post was
  // inserted somewhere further up, or the trip renumbered: the older pages
  // this device holds may lack the one or carry stale keys. They are fetched
  // again when scrolled to; until then the fresh page stands alone.
  if (ordnungAnders(alt, frisch)) return frisch
  // Leere frische Antwort: dann deckt sie nichts ab, und alles Alte bleibt.
  const letzteFrische = frisch.messages.at(-1)
  const unten = letzteFrische === undefined
    ? alt.messages
    : alt.messages.filter((m) => compareMessages(m, letzteFrische) < 0)
  return {
    messages: [...frisch.messages, ...unten],
    next_before: unten.length > 0 ? alt.next_before : frisch.next_before,
    last_read_id: frisch.last_read_id,
    order_rev: frisch.order_rev ?? alt.order_rev,
    as_of: frisch.as_of ?? alt.as_of,
  }
}

/**
 * Did the order change between two answers (#130)? Both must know it: a list
 * without the mark says nothing. One question at three places
 * (`seitenZusammenfuehren`, `useMessages`, `useOlderMessages`).
 */
function ordnungAnders(alt: MessagePage, frisch: MessagePage): boolean {
  return frisch.order_rev !== undefined && alt.order_rev !== undefined && frisch.order_rev !== alt.order_rev
}

/**
 * The changed posts of an answer, set in by id wherever they stand in the
 * loaded range (1.116.0, `ChatChanges` on the server).
 *
 * From use: the author changed a weather card further up, and a reader on
 * the same page kept the old one after every tick, every jump and a
 * restart -- the merge above takes only the newest page fresh. A post that
 * is not in the list is left out: it is either beyond what is loaded, and
 * comes fresh when scrolled to, or new in the order, and `order_rev`
 * fetches the range.
 */
export function aenderungenEinsetzen(seite: MessagePage, changed: Message[] | undefined): MessagePage {
  if (changed === undefined || changed.length === 0) return seite
  const neu = new Map(changed.map((m) => [m.id, m]))
  if (!seite.messages.some((m) => neu.has(m.id))) return seite
  return { ...seite, messages: seite.messages.map((m) => neu.get(m.id) ?? m) }
}

/**
 * Put one's own sent message into the list -- **in place if it is already
 * there**, at the front otherwise (1.75.1).
 *
 * From use, on a poor mobile line: a message stood twice on the sender's
 * side, once for the recipient, and the second copy went away with the next
 * poll. The server had one row; the list had two. Its answer was slow, and
 * the poll (`useMessages`, every fifteen seconds and on focus) brought the
 * stored message first -- the answer was then put in front of it a second
 * time. Since 1.75.0 a second tap after a lost answer gets the existing
 * message back, which makes this more likely, not less.
 *
 * Replaced in place rather than moved: the poll's copy is the server's and
 * already sits where the order puts it.
 */
/**
 * A changed post back in its place, by id (1.86.1).
 *
 * **Whoever changes a post sets the server's answer in; a refetch does
 * not reach it.** The list is merged, not replaced (`seitenZusammenfuehren`):
 * the fresh page wins inside its own range, and whatever lies below it
 * stays as the cache had it. Invalidating after a PATCH therefore refreshed
 * only a card within the newest page -- from use, 1.86.0: "nach dem
 * Speichern wieder der alte Zustand" for the weather card and the poll,
 * while the GIF swap, which already did this, worked. The same reason
 * stands at `MessagesController::delete()`.
 *
 * Not `nachrichtEinsetzen()`: that one asks whether the id is already there
 * and puts a new post in front otherwise; this one demands the id -- a post
 * that is not in the list is not replaced, and the next fetch brings it.
 * The read mark stays as it was: a PATCH knows nothing of who has read.
 */
export function nachrichtErsetzen(
  client: QueryClient,
  tripId: number,
  withUser: number | undefined,
  entwurf: boolean,
  message: Message,
): void {
  client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) =>
    old ? { ...old, messages: old.messages.map((m) => (m.id === message.id ? { ...message, read: m.read } : m)) } : old,
  )
  void client.invalidateQueries({ queryKey: keys.threads })
}

/**
 * After one's own insertion the list is fetched anew at once (#130, 1.115.1).
 * The post is set in at its place above, but an insertion that found no gap
 * numbered the whole trip anew, and the list holds the old keys: the new
 * post stood among them at a wrong place until the next tick (measured in
 * `einfuegensprung.mjs`, case F). The refetch brings the loaded range in the
 * new order (`useMessages`). Not for a post at the end: it moves nothing.
 */
function ordnungGeaendert(client: QueryClient, tripId: number, withUser: number | undefined, entwurf: boolean, message: Message): void {
  if (entwurf || message.inserted !== true) return
  void client.invalidateQueries({ queryKey: keys.messages(tripId, withUser), exact: true })
}

function nachrichtEinsetzen(alt: MessagePage | undefined, message: Message): MessagePage {
  // Without a page there is no read mark from before: one's own message
  // that was just sent is nothing unread.
  if (alt === undefined) return { messages: [message], next_before: null, last_read_id: message.id }
  if (alt.messages.some((m) => m.id === message.id)) {
    return { ...alt, messages: alt.messages.map((m) => (m.id === message.id ? message : m)) }
  }
  // **At its place, not in front** (#130): an inserted post stands between
  // two others. The list is newest first, so it goes before the first row
  // that stands above it.
  const vor = alt.messages.findIndex((m) => compareMessages(m, message) < 0)
  const messages = vor === -1 ? [...alt.messages, message] : [...alt.messages.slice(0, vor), message, ...alt.messages.slice(vor)]
  return { ...alt, messages }
}

/**
 * Berühren sich die beiden Ausschnitte?
 *
 * **Sonst risse die Zusammenführung ein Loch in die Zeitachse.** Sind seit dem
 * letzten Takt mehr Nachrichten dazugekommen, als eine Seite fasst, liegt die
 * älteste der frischen Antwort noch über der neuesten geladenen -- dazwischen
 * fehlt dann alles. Gemessen wird genau das und nicht die Anzahl: eine Seite
 * ist vierzig Nachrichten, aber der Server entscheidet das, nicht der Client.
 */
function beruehrenSich(alt: MessagePage, frisch: MessagePage): boolean {
  const frischAelteste = frisch.messages.at(-1)
  const altNeueste = alt.messages.at(0)
  if (frischAelteste === undefined || altNeueste === undefined) return true
  return compareMessages(frischAelteste, altNeueste) <= 0
}

/**
 * Der Verlauf eines Chats.
 *
 * Geladen wird seitenweise von neu nach alt; die Anzeige dreht das um. Statt
 * `useInfiniteQuery` wird hier bewusst eine flache Liste im Cache gehalten:
 * neue Nachrichten kommen vorne dazu, ältere hinten -- das ist mit einer
 * einzigen Liste einfacher zu denken als mit Seiten, die sich überlappen.
 *
 * **Der Takt fragt nur am neuen Ende, und seine Antwort ersetzt nichts**
 * (#42). Aus dem Betrieb: „wenn man diese mit dem Button ‚ältere Nachrichten'
 * abruft, werden diese angezeigt, aber nach einigen Sekunden verschwinden sie
 * wieder." Genau so war es gebaut: der Abruf holt die neueste Seite, und sein
 * Ergebnis wurde als **ganzer** Cache-Eintrag geschrieben -- alles, was der
 * Knopf angehängt hatte, war damit weg. Drei Auslöser, nicht einer: der Takt
 * hier, `refetchOnWindowFocus` aus `main.tsx` und jeder Neuaufbau.
 *
 * Deshalb liest die Abfrage ihren eigenen Stand und mischt die Antwort ein
 * (`seitenZusammenfuehren`). Das ist dieselbe Bauweise wie in
 * `useOlderMessages`, nur in die andere Richtung -- und dieselbe Regel wie
 * bei den Sendewegen weiter unten, die die Liste seit jeher verändern, statt
 * sie zu ersetzen.
 */
export function useMessages(tripId: number, withUser?: number, enabled = true) {
  const client = useQueryClient()
  return useQuery({
    queryKey: keys.messages(tripId, withUser),
    // Off on the draft page (#85): the same component, a different list.
    enabled,
    queryFn: async () => {
      const vorher = client.getQueryData<MessagePage>(keys.messages(tripId, withUser))
      const ergebnis = await verlauf()
      neueTrenner(client, tripId, vorher, ergebnis)
      return ergebnis
    },
    refetchInterval: 15_000,
  })

  async function verlauf(): Promise<MessagePage> {
    const hole = (before?: number) =>
      api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
        with: withUser,
        before,
      })

    // **What changed since the last answer comes along** (1.116.0). The
    // stamp is the list's, read before the request: whatever changed while
    // it runs is in the answer or in the next one (the server's stamp lies
    // a minute back, `ChatChanges::OVERLAP_SECONDS`).
    const seit = client.getQueryData<MessagePage>(keys.messages(tripId, withUser))?.as_of
    const frisch = await api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
      with: withUser,
      changed_since: seit,
    })
    const alt = client.getQueryData<MessagePage>(keys.messages(tripId, withUser))
    if (alt === undefined || alt.messages.length === 0) return frisch
    // **A changed order fetches the loaded range anew, in one request**
    // (#130, 1.115.1). Each insertion counts the order on; the older pages
    // here may lack the post or carry stale keys. Until 1.115.0 they were
    // dropped and only the fresh page stood -- and whoever was reading
    // further up, the author right after an insertion first of all, lost
    // the very range on screen with the next tick (`einfuegensprung.mjs`,
    // case F: post 10 gone, and the new post out of view). The answer to
    // `until` reaches from the newest down to the oldest loaded row and a
    // page beyond, in the new order, so the same rows come back in their
    // new places and the chat stays where it was. Beyond `JUMP_MOST` rows
    // the server answers less, and the rest is fetched when scrolled to.
    //
    // **The same when the list cannot tell what changed** (1.116.0): too
    // many changed posts for one answer, or a list without a stamp -- the
    // offline store of a device from before 1.116.0 holds one, and its
    // stale posts would otherwise stay for good.
    if (ordnungAnders(alt, frisch) || frisch.changed_overflow === true || alt.as_of === undefined) {
      const aeltester = alt.messages.at(-1)
      if (aeltester === undefined || alt.messages.length <= frisch.messages.length) return frisch
      return api.get<MessagePage>(`/api/trips/${tripId}/messages`, { with: withUser, until: aeltester.id })
    }

    // **Die Lücke schließen, bevor eingemischt wird.** Solange sich die
    // beiden Ausschnitte nicht berühren, wird weiter nach unten geholt --
    // höchstens `LUECKE_MAX_SEITEN` mal. Danach steht der frische
    // Ausschnitt für sich, statt einen Verlauf mit einem Loch zu zeigen.
    let zusammen = frisch
    for (let i = 0; i < LUECKE_MAX_SEITEN && !beruehrenSich(alt, zusammen); i++) {
      const cursor = zusammen.next_before
      if (cursor === null) break
      const weiter = await hole(cursor)
      zusammen = {
        messages: [...zusammen.messages, ...weiter.messages],
        next_before: weiter.next_before,
        last_read_id: zusammen.last_read_id,
        order_rev: zusammen.order_rev,
        as_of: zusammen.as_of,
      }
    }
    if (!beruehrenSich(alt, zusammen)) return zusammen

    // **Merged with the list as it stands now, not as it stood before the
    // loop** (1.93.1). From use: a reader saw the posts of a whole page
    // missing in the middle of the chat -- a day separator, a poll, a map
    // card and the text around them -- and neither the jump nor scrolling
    // reached them. Each round of the loop above is a request; an older
    // page the jump or the button loaded meanwhile was in the cache, and
    // returning the merge with `alt` wrote it away. The jump then went on
    // with its own cursor, and the page it skipped never came back
    // (`loch.mjs`). Reading the cache again here keeps it, since nothing
    // awaits between this line and the return.
    return aenderungenEinsetzen(
      seitenZusammenfuehren(
        client.getQueryData<MessagePage>(keys.messages(tripId, withUser)) ?? alt,
        zusammen,
      ),
      frisch.changed,
    )
  }
}

/**
 * A day separator the list did not know brings the trip and the list of
 * separators anew (#130, 1.115.1). The pill offers "Tage" only when the
 * trip counts a separator (`zeigtTage()`), and the trip has no tick: a
 * reader saw the first separator of a trip in the chat, but could not jump
 * to it until the app was opened again -- found in `einfuegensprung.mjs`,
 * case E. The list behind it is kept for thirty seconds and would be as old.
 */
function neueTrenner(client: QueryClient, tripId: number, vorher: MessagePage | undefined, jetzt: MessagePage): void {
  if (vorher === undefined) return
  const bekannt = new Set(vorher.messages.map((m) => m.id))
  if (!jetzt.messages.some((m) => m.day_separator != null && !bekannt.has(m.id))) return
  void client.invalidateQueries({ queryKey: keys.trip(tripId) })
  void client.invalidateQueries({ queryKey: keys.tagestrenner(tripId) })
}

/**
 * An older page: a cursor, or a cursor and the post a jump wants (`until`,
 * 1.102.0). With `until` the server answers down to that post and one page
 * further in one request -- the jump used to ask page by page, forty posts
 * a round trip, and from the list of polls that was six or seven of them.
 */
export type OlderPage = number | { before: number; until: number }
/** The answer of an older page, with when it was asked for (see `trimmedAt`). */
export type OlderAnswer = { page: MessagePage; started: number }

/**
 * When a chat's history was last trimmed on leaving (1.112.0, review),
 * per list. An older page still under way at that moment answers into the
 * trimmed list: its cursor is the cursor of the first page, which the trim
 * keeps, so the join below would take it -- a thousand rows of a jump back
 * into the chat just closed, and the trimming of 1.111.0 undone (measured in
 * `jumptarget.mjs`: 310 rows on the way back). A page asked for before the
 * trim is dropped; the next load asks from the trimmed list's end.
 */
const trimmedAt = new Map<string, number>()

export function useOlderMessages(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (request: OlderPage) => {
      const started = performance.now()
      const page = await api.get<MessagePage>(`/api/trips/${tripId}/messages`, {
        with: withUser,
        ...(typeof request === 'number' ? { before: request } : request),
      })
      return { page, started } as OlderAnswer
    },
    onSuccess: ({ page, started }, request) => {
      if ((trimmedAt.get(keys.messages(tripId, withUser).join('/')) ?? -1) > started) return
      const before = typeof request === 'number' ? request : request.before
      const listKey = keys.messages(tripId, withUser)
      let orderChanged = false
      client.setQueryData<MessagePage>(listKey, (old) => {
        if (old === undefined) return page
        // **Only a page that joins the end of the list** (1.93.1). A page
        // asked for with a cursor beyond the list's end would leave a gap
        // between the two -- the rows in it are never loaded again, since
        // every later cursor lies below them. Dropped here, the next load
        // asks from the list's own end and fills it. `next_before` is the id
        // of the oldest loaded row (`MessagesController::list()`), so a
        // smaller cursor is exactly that gap; with none left, nothing older
        // exists to join.
        // Since #130 the cursor is an id but the order is the key: a page
        // joins when it was asked from a row the list has -- its end, or a
        // row above it when another load (the unread edge, measured in
        // `jumptarget.mjs`) has meanwhile reached further. Only what lies
        // below the list's end is taken. A cursor the list does not hold
        // would leave a gap. A changed order (`order_rev`) does not join
        // either: its rows may stand anywhere in what is loaded.
        if (old.next_before === null) return old
        if (before !== old.next_before && !old.messages.some((m) => m.id === before)) return old
        if (ordnungAnders(old, page)) {
          orderChanged = true
          return old
        }
        const ende = old.messages.at(-1)
        const neu = page.messages.filter((m) =>
          !old.messages.some((o) => o.id === m.id) && (ende === undefined || compareMessages(m, ende) < 0))
        return {
          // **Not a row twice** (1.85.0). Two roads load an older page
          // -- the jump to `?m=` and the search for the unread edge --
          // and both can ask for the same cursor in one draw, before
          // `isPending` is true for either. Measured: a reader opening
          // `?m=` with sixty unread posts had the oldest page twice, and
          // `#msg-1` stood twice in the chat. Whoever writes into a list
          // another road fills as well asks for the id first.
          messages: [...old.messages, ...neu],
          // A page that brought nothing new reached no further than the list.
          next_before: neu.length > 0 ? page.next_before : old.next_before,
          // **Der Lesestand bleibt der von vorhin.** Er gehört dem Öffnen,
          // nicht dem Nachladen -- sonst verschöbe sich die Trennlinie beim
          // Blättern nach hinten, obwohl niemand etwas gelesen hat.
          last_read_id: old.last_read_id,
          // **And the order goes with it** (#130, 1.115.1). Without this line
          // the list lost its `order_rev` with the first older page, and both
          // checks ("has the order changed?") compared against undefined and
          // never dropped anything: an insertion further up stayed missing in
          // the range the reader had loaded (`einfuegensprung.mjs`, case B).
          order_rev: old.order_rev,
          // The stamp stays the list's: the tick asks for changes since its
          // own last answer, which covers this page too.
          as_of: old.as_of,
        }
      })
      // **A dropped page asks for the newest one at once** (#130, 1.115.1).
      // The page came with an order the list does not know yet: an insertion
      // the tick has not seen. Dropped and left at that, the tap on "older"
      // did nothing until the next tick, up to fifteen seconds -- a silent
      // refusal. The newest page brings the new `order_rev`, `useMessages`
      // drops the older pages, and the next tap loads them in the new order
      // (`einfuegensprung.mjs`, case C).
      if (orderChanged) void client.invalidateQueries({ queryKey: listKey, exact: true })
    },
  })
}

/**
 * How many posts of a chat stay in the cache when it is left (1.111.0).
 *
 * From use, with a recording on the iPhone: back from the trip's page into
 * the chat, eight seconds; after reloading the app, the same way took a
 * moment. The list only grows -- every jump far back (`?until=`), every
 * older page, the search for the unread edge -- and the chat drew all of it
 * again each time it opened: three hundred posts with their pictures in the
 * test bed, over four seconds with the CPU slowed tenfold. Reloading the app
 * starts with the newest page, which is why it was quick.
 *
 * So leaving a chat trims its list to the newest forty and sets the cursor
 * to the oldest of them, which is what the server's `next_before` would say
 * (`MessagesController::list()`). Forty is the server's page by default;
 * where the page is larger, the next poll fills the newest page and the
 * merge keeps the rest (`seitenZusammenfuehren`). A jump into the chat at
 * an older post asks for it in one request, as from a fresh start.
 */
const PAGE_SIZE_FALLBACK = 40

/**
 * The server's page (`MESSAGES_PAGE_SIZE`), from `/api/settings` like
 * `text_limits` (1.112.0, review) -- here stood a copy of its default.
 */
function pageSize(client: QueryClient): number {
  return client.getQueryData<SettingsResponse>(keys.settings)?.page_size ?? PAGE_SIZE_FALLBACK
}

function trimHistory(client: QueryClient, tripId: number, withUser?: number): void {
  trimmedAt.set(keys.messages(tripId, withUser).join('/'), performance.now())
  const keep = pageSize(client)
  client.setQueryData<MessagePage>(keys.messages(tripId, withUser), (old) => {
    if (old === undefined || old.messages.length <= keep) return old
    const kept = old.messages.slice(0, keep)
    return { ...old, messages: kept, next_before: kept[kept.length - 1]!.id }
  })
}

/** Trims the chat's list when it is left, or when another chat takes its place. */
export function useHistoryTrimmedOnLeaving(tripId: number, withUser: number | undefined, off: boolean): void {
  const client = useQueryClient()
  useEffect(() => {
    if (off) return
    return () => trimHistory(client, tripId, withUser)
  }, [client, tripId, withUser, off])
}

/**
 * Which list a bubble belongs to (#85): the chat, or the draft.
 *
 * One place for the four hooks that change a message in its list -- send,
 * edit, delete, react -- so none of them can write a draft into the chat.
 */
function verlaufKey(tripId: number, withUser: number | undefined, entwurf: boolean) {
  return entwurf ? keys.entwurf(tripId) : keys.messages(tripId, withUser)
}

/**
 * The draft chat (#85, 1.21.0): the traveller's unpublished posts, whole.
 *
 * The same shape as a page of the chat, so `ThreadPage` shows it with the
 * same bubbles. No polling: nobody else writes into a draft.
 */
export function useEntwurf(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.entwurf(tripId),
    queryFn: () => api.get<MessagePage>(`/api/trips/${tripId}/draft`),
    enabled: enabled && tripId > 0,
  })
}

/** The order of the draft anew (#85): all ids, as a person reads them. */
export function useEntwurfOrdnen(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.patch<MessagePage>(`/api/trips/${tripId}/draft/order`, { ids }),
    onSuccess: (seite) => {
      client.setQueryData<MessagePage>(keys.entwurf(tripId), seite)
    },
  })
}

/**
 * Publish the draft up to a post (#85). Afterwards everything that shows
 * the chat is stale: the history, the list, the trip (its draft count), the
 * draft itself -- and the map, if points went along.
 */
export function useVeroeffentlichen(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { untilId: number; withHighlights: boolean }) =>
      api.post<{ published: number; highlights: number; first_id: number | null; last_id: number | null }>(
        `/api/trips/${tripId}/draft/publish`,
        { until_id: input.untilId, with_highlights: input.withHighlights },
      ),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.entwurf(tripId) }),
        client.invalidateQueries({ queryKey: keys.messages(tripId) }),
        client.invalidateQueries({ queryKey: keys.trip(tripId) }),
        client.invalidateQueries({ queryKey: keys.highlights(tripId) }),
        client.invalidateQueries({ queryKey: keys.threads }),
      ])
    },
  })
}

/**
 * Lädt ein verkleinertes Foto hoch, noch bevor der Beitrag abgeschickt ist.
 *
 * Aufnahmeort und -zeit gehen als Kopfzeilen mit — der Server legt sie an der
 * Datei ab, und die Karte baut daraus später einen Vorschlag, ohne das Bild
 * noch einmal zu lesen.
 */
export function useUploadMedia(tripId: number) {
  return useMutation({
    /**
     * **Diese Absage steht schon dort, wo der Finger war** (Codereview E2):
     * das Formular und die Eingabezeile zeigen sie als Absatz an der Kachel.
     * Das globale Fangnetz in `main.tsx` haelt sich hier deshalb heraus --
     * zwei Meldungen uebereinander sind eine zu viel.
     */
    meta: { eigeneMeldung: true },
    mutationFn: async (input: {
      blob: Blob
      aufnahme?: Aufnahme
      /** Das Vorschaubild aus `prepareImage()` (#57) — freiwillig. */
      thumb?: Blob | null
      onProgress?: (anteil: number) => void
    }): Promise<Media> => {
      const medium = await api.upload<Media>(
        `/api/trips/${tripId}/media`,
        input.blob,
        input.onProgress,
        aufnahmeKopf(input.aufnahme),
      )

      /**
       * **Das Vorschaubild geht hinterher, und sein Fehlschlag ist keiner.**
       *
       * Es ist eine Beschleunigung, kein Inhalt: kommt es nicht durch, steht
       * in der Kachel das Original — langsamer, aber vollständig. Ein Foto,
       * das im Ausgangskorb hängen bleibt, weil sein Bildchen nicht ankam,
       * wäre der schlechtere Handel.
       *
       * **Und hier, nicht an jeder Aufrufstelle.** Das Standbild eines Videos
       * hat genau diese Lehre schon einmal gekostet: es stand an zwei Stellen,
       * die auseinanderliefen, und wurde deshalb in `uploadVideo()`
       * zusammengezogen. Was es nur einmal gibt, kann nicht auseinanderlaufen.
       */
      return (await vorschauHochladen(medium.id, input.thumb)) ?? medium
    },
  })
}

/**
 * Die eigenen Bilder dieser Reise — für die Auswahl „Aus dieser Reise".
 *
 * Nur wenn die Auswahl offen ist: eine Reise kann Hunderte Bilder haben, und
 * niemand braucht die Liste, solange er nur ein Highlight ansieht.
 */
export function useReisefotos(tripId: number, enabled: boolean, ziel?: Reisefotoziel) {
  return useQuery({
    queryKey: keys.reisefotos(tripId, ziel),
    // The target goes along (1.53.3): the server says what matches and
    // caps the rest; `undefined` values are left out of the query string.
    queryFn: () =>
      api.get<Reisefotos>(`/api/trips/${tripId}/media`, {
        lat: ziel?.lat,
        lon: ziel?.lon,
        at: ziel?.at,
        highlight: ziel?.highlight,
      }),
    enabled,
    staleTime: 30_000,
    /*
     * **Fresh on every opening** (1.52.0). The list says where each picture
     * hangs, and that changes with every save at the point, every move into
     * a spot, every post -- and none of those invalidates this key. With
     * the picker opened twice within half a minute, the second opening
     * showed the first one's list: a photo just hung on the point was not
     * in "Aus diesem POI". Measured in `spots.mjs`. The picker mounts when
     * it opens, so mounting is the moment to ask again.
     */
    refetchOnMount: 'always',
  })
}

/**
 * The thread search (1.17.0). Only from two characters on -- below that the
 * server refuses, and asking would be a request per keystroke for nothing.
 */
export function useSuche(tripId: number, q: string) {
  const wort = q.trim()
  return useQuery({
    queryKey: keys.suche(tripId, wort),
    queryFn: () => api.get<SearchPage>(`/api/trips/${tripId}/search`, { q: wort }),
    enabled: Number.isFinite(tripId) && wort.length >= 2,
    staleTime: 30_000,
    placeholderData: (alt) => alt,
  })
}

/** All pictures and videos of a trip (1.20.0), in pages by id, newest first. */
export function useGalerie(tripId: number) {
  return useInfiniteQuery({
    queryKey: keys.galerie(tripId),
    queryFn: ({ pageParam }) =>
      api.get<Galerieseite>(`/api/trips/${tripId}/gallery`, { before: pageParam }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (letzte) => letzte.next_before ?? undefined,
    enabled: Number.isFinite(tripId),
    staleTime: 30_000,
  })
}

/** The days of a chat (1.19.0), asked in the device's clock -- on a tap, never before. */
export function useTage(tripId: number, withUser: number | undefined, tz: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.tage(tripId, withUser, tz),
    queryFn: () => api.get<Tagesliste>(`/api/trips/${tripId}/days`, { with: withUser, tz }),
    enabled: enabled && Number.isFinite(tripId),
    staleTime: 30_000,
  })
}

/**
 * Wirft ein hochgeladenes Foto weg, das doch nicht mitsoll.
 *
 * `fromHighlight` ist der eine Weg, auf dem auch ein Foto weggeht, das schon
 * an einem Kartenpunkt hängt: das Kreuz im Formular des Punktes fragt vorher,
 * und Wegnehmen *ist* dort Löschen. Jeder andere Aufruf bekommt vom Server
 * ein 409 -- die Angabe entscheidet nichts über die Berechtigung, nur
 * darüber, ob schon jemand gefragt hat.
 */
export function useDiscardMedia() {
  return useMutation({
    /**
     * **Diese Absage steht schon dort, wo der Finger war** (Codereview E2):
     * das Formular und die Eingabezeile zeigen sie als Absatz an der Kachel.
     * Das globale Fangnetz in `main.tsx` haelt sich hier deshalb heraus --
     * zwei Meldungen uebereinander sind eine zu viel.
     */
    meta: { eigeneMeldung: true },
    mutationFn: ({ id, fromHighlight }: { id: number; fromHighlight?: boolean }) =>
      api.delete<void>(
        `/api/media/${id}`,
        fromHighlight === true ? { from_highlight: true } : undefined,
      ),
  })
}

export function useSendMessage(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    // The fields of a post stand once, at `nachrichtRumpf()` (1.112.0, review).
    mutationFn: (input: NachrichtTeile) =>
      api.post<Message>(
        `/api/trips/${tripId}/messages`,
        // **Der Rumpf wird an einer Stelle gebaut** (`api/nachrichtRumpf.ts`).
        // Der Ausgangskorb baut denselben; standen sie an zwei Stellen, bekäme
        // eine von beiden das nächste Feld nicht mit.
        nachrichtRumpf(input),
        // Im Antwortverlauf schreibt der Autor an genau diesen einen
        // Menschen. Dass daraus eine private Nachricht wird, entscheidet der
        // Server -- hier steht nur, welcher Verlauf offen ist.
        { with: withUser },
      ),
    onSuccess: (message, input) => {
      // A draft before or behind another one (#85, #130) is not at the end,
      // and the draft is ordered by its own positions: the list comes back
      // from the server instead of being patched here. In the chat the post
      // goes to its place by `sort_key` (`nachrichtEinsetzen()`).
      if (entwurf && input.insertion) {
        void client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
        void client.invalidateQueries({ queryKey: keys.trip(tripId) })
        return
      }
      client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) =>
        nachrichtEinsetzen(old, message),
      )
      ordnungGeaendert(client, tripId, withUser, entwurf, message)
      if (entwurf) void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * The day separators of a trip (#113): for the list behind the calendar in
 * the pill, and for the proposal (next number, today, the trip's word) in
 * the sheet. Asked on a tap, never before (`enabled`).
 */
export function useTagestrenner(tripId: number, enabled: boolean, insertion?: Insertion | null) {
  return useQuery({
    // The proposal depends on the place (#130): the number between the
    // neighbours, the chosen post's day.
    queryKey: insertion ? [...keys.tagestrenner(tripId), insertion.where, insertion.id] : keys.tagestrenner(tripId),
    queryFn: () => api.get<Tagestrennerliste>(`/api/trips/${tripId}/tagestrenner`, insertionFields(insertion)),
    enabled: enabled && Number.isFinite(tripId),
    staleTime: insertion ? 0 : 30_000,
  })
}

/**
 * A new day separator, or a change of one (#113, #130).
 *
 * **A separator is a post of its own** (1.115.0), made like a weather card:
 * its own route, at the end or before or after a chosen post, or into the
 * draft. The answer is the whole post, set into the list at its place.
 * Removing it is deleting the post (`useDeleteMessages`); the road that hung
 * a line on another post is gone.
 */
export function useTagestrennerAendern(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (schritt:
      | { aktion: 'anlegen'; angaben: TagestrennerAngaben; insertion: Insertion | null; renumber: boolean; clientKey: string }
      | { aktion: 'aendern'; messageId: number; angaben: TagestrennerAngaben }): Promise<{ message_id: number; day_separator: DaySeparator } | Message> => {
      if (schritt.aktion === 'anlegen') {
        return api.post<Message>(`/api/trips/${tripId}/tagestrenner`, {
          day_separator: schritt.angaben,
          ...insertionFields(schritt.insertion),
          ...(entwurf ? { draft: true } : {}),
          renumber_following: schritt.renumber,
          client_key: schritt.clientKey,
          tz_offset_min: versatzDesGeraets(),
        })
      }
      return api.patch<{ message_id: number; day_separator: DaySeparator }>(
        `/api/trips/${tripId}/tagestrenner/${schritt.messageId}`,
        schritt.angaben,
      )
    },
    onSuccess: (antwort, schritt) => {
      if (istNachricht(antwort)) {
        if (entwurf && schritt.aktion === 'anlegen' && schritt.insertion) {
          void client.invalidateQueries({ queryKey: keys.entwurf(tripId) })
        } else {
          client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) => nachrichtEinsetzen(old, antwort))
          ordnungGeaendert(client, tripId, withUser, entwurf, antwort)
        }
        // Counting on may have changed the numbers of later lines.
        if (schritt.aktion === 'anlegen' && schritt.renumber) void client.invalidateQueries({ queryKey: keys.messages(tripId, withUser) })
      } else {
        const trenner = antwort.day_separator
        client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) =>
          old === undefined
            ? old
            : { ...old, messages: old.messages.map((m) => (m.id === antwort.message_id ? { ...m, day_separator: trenner } : m)) },
        )
      }
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.tagestrenner(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * A post let go of pictures (1.88.1) -- by a change or by being deleted.
 *
 * **Every list that shows a picture of the trip asks anew**: the trip's
 * photos (`FotosPage`), the picker "Aus dieser Reise" and the points, whose
 * pictures say whether they also stand in the chat (`im_chat`). Found while
 * rebuilding a report on 1.88.0, not reported itself: one of four pictures
 * taken out of a post, and the photo page still showed it for up to 30 s --
 * it keeps its pages that long, and nothing told it.
 * Deleting a post had the same gap since the photo page exists (1.20.0).
 */
function bilderGeaendert(client: QueryClient, tripId: number) {
  void client.invalidateQueries({ queryKey: keys.galerie(tripId) })
  void client.invalidateQueries({ queryKey: keys.reisefotos(tripId) })
  void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
}

export function useEditMessage(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    // `media_ids` only when pictures leave (1.88.0): the list names what
    // the post keeps, and without it the server leaves them as they are.
    mutationFn: (input: { id: number; body: string; media_ids?: number[] }) =>
      api.patch<Message>(`/api/messages/${input.id}`, {
        body: input.body,
        ...(input.media_ids !== undefined ? { media_ids: input.media_ids } : {}),
      }),
    // The changed post back in its place instead of the whole list anew --
    // otherwise the view jumps on sending (`nachrichtErsetzen`).
    onSuccess: (message, input) => {
      nachrichtErsetzen(client, tripId, withUser, entwurf, message)
      if (input.media_ids !== undefined) bilderGeaendert(client, tripId)
    },
  })
}

/**
 * Setzt, ersetzt oder nimmt eine Reaktion zurück.
 *
 * Dasselbe Emoji noch einmal nimmt es zurück, ein anderes ersetzt es -- eine
 * Person hat je Ziel genau eine Reaktion. Der Server schickt den neuen Stand
 * der ganzen Nachricht zurück; hier wird nur diese eine ersetzt, damit der
 * Verlauf nicht springt.
 */
export function useToggleReaction(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { messageId: number; emoji: string; mediaId?: number }) =>
      api.post<{ message_id: number; reactions: Reaction[] }>(
        `/api/messages/${input.messageId}/reactions`,
        { emoji: input.emoji, media_id: input.mediaId ?? 0 },
      ),
    onSuccess: ({ message_id, reactions }) => {
      client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) =>
        old
          ? {
              ...old,
              messages: old.messages.map((m) =>
                m.id === message_id ? { ...m, reactions } : m,
              ),
            }
          : old,
      )
    },
  })
}

/**
 * Mehrere Nachrichten auf einmal loeschen (#30).
 *
 * **Kein Sammelendpunkt, sondern N Aufrufe der bestehenden Route.** Wem eine
 * Nachricht gehoert, entscheidet der Server in genau einer Zeile
 * (`WHERE id = :id AND author_id = :user`); eine zweite Route muesste dieselbe
 * Regel ein zweites Mal hinschreiben, und das ist hier schon dreimal
 * auseinandergelaufen. Zehn Aufrufe kosten auf einem Freizeit-Hosting nichts,
 * eine doppelte Regel kostet irgendwann alles.
 *
 * **Der Reihe nach und nicht gleichzeitig:** ein Paket mit knappen Grenzen
 * dankt es, und die Reihenfolge macht die Zahl am Ende ehrlich.
 *
 * **Was schiefgeht, wird gezaehlt und gesagt.** Der Aufrufer bekommt beide
 * Zahlen und kann „3 von 5 geloescht" hinstellen, statt so zu tun, als sei
 * alles weg. Was nicht durchkam, bleibt markiert.
 *
 * **Und jede geloeschte wird an ihren Platz zurueckgelegt, nicht nur
 * aufgefrischt.**
 *
 * Aus dem Betrieb zu #30: „funktioniert meistens, aber nicht immer", geloescht
 * wurde im Privatchat eines Lesers. Ein Neuabruf **ersetzt** den Verlauf nicht,
 * er fuehrt zusammen (`seitenZusammenfuehren`, aus #42): innerhalb der frischen
 * Seite gilt die frische Antwort, darunter bleibt der geladene Ausschnitt
 * stehen -- der Teil, den „Aeltere Nachrichten" geholt hat. Wer weiter zurueck
 * loescht, bekam den alten Stand aus dem eigenen Zwischenspeicher wieder
 * untergemischt: geloescht war sie, zu sehen blieb sie trotzdem, und der Takt
 * alle fuenfzehn Sekunden wiederholte das.
 *
 * `setQueryData` trifft die Nachricht an ihrer Stelle, egal wie weit unten die
 * liegt. **Dieselbe Machart wie beim Aendern und beim Reagieren** -- und dort
 * war es die ganze Zeit richtig; Loeschen war die eine Stelle, die nur
 * aufgefrischt hat.
 *
 * Was eine geloeschte Nachricht ist, sagt dabei der **Server**: er gibt sie
 * zurueck, und was hier ankommt, wird hingelegt. Die Form im Browser
 * nachzubauen waere dieselbe Regel an zwei Stellen.
 *
 * Aufgefrischt wird **einmal** am Ende statt nach jedem Loeschen -- sonst
 * zeichnet der Verlauf bei zehn Nachrichten zehnmal neu.
 */
/**
 * Posts moved before or after another (#130, 1.117.0, `ChatOrder::move()`).
 * The order of the trip changes, so every list of it is fetched anew: the
 * chat and the private chats (replies go with their post), the days of the
 * pill and the separators -- the same as after an insertion, only for all
 * lists at once, since a move touches more than one row.
 */
export function useMoveMessages(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { ids: number[]; target_id: number; place: 'before' | 'after' }) =>
      api.post<{ moved: number[]; order_rev: number }>(`/api/trips/${tripId}/messages/move`, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['messages', tripId] })
      void client.invalidateQueries({ queryKey: ['tage', tripId] })
      void client.invalidateQueries({ queryKey: keys.tagestrenner(tripId) })
    },
  })
}

export function useDeleteMessages(tripId: number, withUser?: number, entwurf = false) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (
      ids: number[],
    ): Promise<{ weg: Message[]; geblieben: number[] }> => {
      const weg: Message[] = []
      const geblieben: number[] = []
      for (const id of ids) {
        try {
          weg.push(await api.delete<Message>(`/api/messages/${id}`))
        } catch {
          geblieben.push(id)
        }
      }
      return { weg, geblieben }
    },
    onSuccess: ({ weg }) => {
      if (weg.length > 0) {
        const geloescht = new Map(weg.map((m) => [m.id, m]))
        client.setQueryData<MessagePage>(verlaufKey(tripId, withUser, entwurf), (old) => {
          if (!old) return old
          // A day separator goes with its post (#130): it is the post.
          const messages = old.messages.map((m) => geloescht.get(m.id) ?? m)
          return { ...old, messages }
        })
        if (entwurf) void client.invalidateQueries({ queryKey: keys.trip(tripId) })
        void client.invalidateQueries({ queryKey: keys.tagestrenner(tripId) })
        bilderGeaendert(client, tripId)
      }
      // Die Vorschau in der Chatliste haengt am Verlauf und nicht an dieser
      // einen Nachricht -- die holt sich der Server.
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Was ein Zurücksetzen der Reise kosten würde (#12).
 *
 * **Erst auf Verlangen**, nicht beim Öffnen der Reiseseite: es sind vier
 * Zählungen über den ganzen Inhalt, und die meisten Besuche der Seite haben
 * damit nichts zu tun.
 *
 * `staleTime: 0`: zwischen Ansehen und Bestätigen kann etwas dazugekommen
 * sein, und die Zahlen sollen die von jetzt sein.
 */
export function useResetVorschau(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.resetPreview(tripId),
    queryFn: () => api.get<TripResetVorschau>(`/api/trips/${tripId}/reset`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Die Reise auf Anfang stellen (#12) — der Inhalt geht, die Reise bleibt.
 *
 * **Danach ist alles ungültig, was aus dieser Reise stammt.** Deshalb hier
 * ausnahmsweise die breite Auffrischung: Chatliste, Route, Punkte, Fotos, die
 * Reise selbst und der Speicherstand. Einzeln nachzuhalten, was sich geändert
 * hat, wäre dieselbe Liste ein zweites Mal — und die eine, die dabei vergessen
 * wird, zeigt hinterher Nachrichten an, die es nicht mehr gibt.
 *
 * **Der Verlauf wird weggeworfen und nicht aufgefrischt**, und das ist der
 * Unterschied, an dem es hing.
 *
 * Aus dem Betrieb, beim ersten Versuch am Gerät: „die Bilder / Videos sind
 * weg, die Nachrichten nicht." Auf dem Server waren sie sehr wohl weg — der
 * Browser hielt sie fest. `useMessages` **ersetzt** den Verlauf nicht, es
 * führt zusammen (`seitenZusammenfuehren`, aus #42), und für eine leere
 * frische Antwort steht dort ausdrücklich: „dann deckt sie nichts ab, und
 * alles Alte bleibt." Genau der Fall tritt nach dem Zurücksetzen ein. Eine
 * Auffrischung holt sich also die Leere und mischt die alten Nachrichten
 * wieder darunter.
 *
 * **Und Neuladen half nicht.** Der Verlauf steht in der Positivliste des
 * Vorrats (`vorrat.ts`) und wird vor dem ersten Zeichnen wieder eingespielt —
 * er überlebt damit auch das Schließen der App. Was nicht aus dem
 * Zwischenspeicher genommen wird, kommt sieben Tage lang zurück.
 *
 * `removeQueries` nimmt dem Neuabruf das Alte weg, an dem er sonst festhält.
 * **Dieselbe Zusammenführung wie bei #30, nur von der anderen Seite** — dort
 * überlebte eine einzelne gelöschte Nachricht, hier überleben alle.
 */
/**
 * The suggestions of the trip (#100): how many there are, and away with
 * them. The number is asked fresh whenever the card shows, like the reset's
 * preview; after deleting, the map's points and the number are asked again.
 */
export function useVorschlaegeZahl(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.vorschlaege(tripId),
    queryFn: () => api.get<VorschlaegeZahl>(`/api/trips/${tripId}/suggestions`),
    enabled,
    staleTime: 0,
  })
}

export function useVorschlaegeLoeschen(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<VorschlaegeZahl>(`/api/trips/${tripId}/suggestions`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      await client.invalidateQueries({ queryKey: keys.vorschlaege(tripId) })
    },
  })
}

/**
 * The protected regions (#99): administration only; the server answers
 * nobody else, so the card asks only where the settings carry a ceiling.
 */
export function useRegionen(enabled: boolean) {
  return useQuery({
    queryKey: keys.regionen,
    queryFn: () => api.get<RegionsResponse>('/api/regions'),
    enabled,
    staleTime: 60_000,
  })
}

export function useRegionAnlegen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (region: { name: string; lat: number; lon: number; radius_m: number }) =>
      api.post<RegionsResponse & { id: number }>('/api/regions', region),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.regionen })
    },
  })
}

export function useRegionEntfernen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<RegionsResponse>(`/api/regions/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.regionen })
    },
  })
}

export function useDeleteVorschau(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.deletePreview(tripId),
    queryFn: () => api.get<TripDeleteVorschau>(`/api/trips/${tripId}/delete`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Delete the trip for good (#15).
 *
 * Afterwards nothing of it may stay in memory or on disk: the same trap as
 * after a reset (see `useTripReset`), plus the trip itself in the lists. The
 * caller leaves the page -- what confirms the deletion must not live inside
 * what the deletion removes (§6dc).
 */
export function useTripDelete(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<TripDeleteBefund>(`/api/trips/${tripId}/delete`, { code }),
    onSuccess: () => {
      // **Ein Präfix und kein Schlüssel**: `keys.messages()` trägt das
      // Gegenüber als dritten Abschnitt, und weg muss jedes Gespräch dieser
      // Reise.
      client.removeQueries({ queryKey: ['messages', tripId] })
      client.removeQueries({ queryKey: keys.trip(tripId) })
      void vorratLeeren()
      for (const key of [keys.threads, keys.trips, keys.speicher, keys.people]) {
        void client.invalidateQueries({ queryKey: key })
      }
    },
  })
}

export function useTripReset(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<TripResetBefund>(`/api/trips/${tripId}/reset`, { code }),
    onSuccess: () => {
      // **Ein Präfix und kein Schlüssel**: `keys.messages()` trägt das
      // Gegenüber als dritten Abschnitt, und weg muss jedes Gespräch dieser
      // Reise.
      client.removeQueries({ queryKey: ['messages', tripId] })
      /*
       * **Und der Vorrat auf der Platte gleich mit.**
       *
       * `removeQueries` räumt den Arbeitsspeicher; auf die Platte kommt der
       * neue Stand erst mit dem nächsten gedrosselten Schreibvorgang
       * (`vorrat.ts`, eine Sekunde). Wer in dieser Lücke neu lädt, bekommt den
       * alten Satz **vor dem ersten Zeichnen** wieder eingespielt -- genau
       * das, was hier gerade weggeworfen wurde.
       *
       * **Das ist die Lücke, die #12 offen gelassen hat.** Der Prüflauf war
       * daran mal grün und mal rot, je nachdem, welche der beiden Uhren
       * gewann; gefunden wurde es erst, als er den Vorrat selbst mitgemessen
       * hat statt nur die Sprechblasen. Er zählt jetzt beides, und die Zahlen
       * stimmen überein.
       *
       * Der ganze Vorrat und nicht nur diese Reise: er liegt als **ein** Satz
       * auf der Platte. Was fehlt, holt der nächste Abruf.
       */
      void vorratLeeren()
      for (const key of [
        keys.threads,
        keys.trips,
        keys.trip(tripId),
        keys.tracks(tripId),
        keys.highlights(tripId),
        keys.reisefotos(tripId),
        keys.speicher,
        ['reset-vorschau', tripId],
      ]) {
        void client.invalidateQueries({ queryKey: key })
      }
    },
  })
}

/**
 * Mark the chat read -- **up to the newest message on screen** (1.79.0).
 *
 * Until here it sent nothing, and the server took "now". A message that
 * arrived between loading the list and this call counted as read although it
 * never stood on screen: no number in the list, no sign that something is
 * new. The prüfstand found it (`ansichtsfarben`, one run in three) once the
 * start got faster; the server has taken `until` all along.
 */
export function useMarkRead(tripId: number, withUser?: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (until: string) =>
      api.post<void>(`/api/trips/${tripId}/read`, { until }, { with: withUser }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Die Kontenliste -- der Server gibt sie nur der Verwaltung. */
export function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: keys.people,
    queryFn: () => api.get<Person[]>('/api/people'),
    enabled,
  })
}

/** Entfernt jemanden aus einer Reise -- nur der Absender darf das. */
/**
 * Wer sich dieser Reise zuordnen lässt (#36) -- nur für den Absender.
 *
 * `staleTime: 0`: die Liste ändert sich mit jedem Häkchen, und ein Stand von
 * vorhin wäre genau die Zeile, die man gerade angetippt hat.
 */
export function useCandidates(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.candidates(tripId),
    queryFn: () => api.get<Candidate[]>(`/api/trips/${tripId}/candidates`),
    enabled,
    staleTime: 0,
  })
}

/**
 * Ordnet jemanden ohne Einladungslink zu (#36).
 *
 * Wirkt sofort -- kein Sammeln, kein Speichern-Knopf. Ein Häkchen, das erst
 * später wirkt, ist die stille Absage, die dieses Projekt nicht will.
 */
export function useAddMember(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<void>(`/api/trips/${tripId}/members/${userId}`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.candidates(tripId) })
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.requests(tripId) })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Verschickt die Willkommensnachricht (#36).
 *
 * Ohne `userId` an alle Empfänger, die sie noch nicht haben; mit `userId` an
 * genau den einen. Zurück kommt, wie viele es waren -- „es ist etwas
 * passiert" wäre hier zu wenig, gerade wenn es null ist.
 */
export function useSendWelcome(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId?: number) =>
      api.post<{ sent: number }>(
        `/api/trips/${tripId}/welcome`,
        userId === undefined ? {} : { user: userId },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
      void client.invalidateQueries({ queryKey: keys.messages(tripId) })
    },
  })
}

export function useRemoveMember(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) =>
      api.delete<void>(`/api/trips/${tripId}/members/${userId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/** Legt ein Konto still oder gibt es wieder frei -- nur die Verwaltung. */
/**
 * Löscht ein Konto endgültig -- nur für die, an denen nichts hängt.
 *
 * Der Server prüft das noch einmal selbst; hier wird der Knopf gar nicht
 * erst angeboten, wo er scheitern würde.
 */
/**
 * Was ein Löschen kosten würde -- gezählt vom Server (#47).
 *
 * `staleTime: 0`: die Zahlen stehen in einer Rückfrage, die etwas Endgültiges
 * ankündigt. Eine Sekunde alt ist hier zu alt.
 */
export function useLoeschvorschau(id: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.accountDeletePreview(id),
    queryFn: () => api.get<Loeschvorschau>(`/api/people/${id}/loeschvorschau`),
    enabled,
    staleTime: 0,
  })
}

export function useDeletePerson() {
  const client = useQueryClient()
  return useMutation({
    // Die abgetippte E-Mail geht im Rumpf mit -- als Suchparameter stünde eine
    // Adresse in jedem Server- und Proxy-Protokoll.
    mutationFn: (input: { id: number; email: string }) =>
      api.delete<Loeschbefund>(`/api/people/${input.id}`, { email: input.email }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

export function useSetDisabled() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; disabled: boolean }) =>
      api.post<{ id: number; disabled: boolean }>(`/api/people/${input.id}/disabled`, {
        disabled: input.disabled,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/** Gibt einem Admin das Merkmal des Supervisors oder nimmt es (#62). */
export function useSetSupervisor() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; supervisor: boolean }) =>
      api.post<{ id: number; supervisor: boolean }>(`/api/people/${input.id}/supervisor`, {
        supervisor: input.supervisor,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/**
 * Die Werte der `.env` für den Supervisor (#62). `staleTime: 0`: wer die
 * Seite öffnet, will den Stand der Datei, nicht den von vorhin.
 */
export function useEnv(enabled: boolean) {
  return useQuery({
    queryKey: keys.envCatalogue,
    queryFn: () => api.get<EnvSeite>('/api/operations/env'),
    enabled,
    staleTime: 0,
  })
}

/** Das Protokoll der Verwaltungshandlungen (1.7.0), die letzten hundert, neueste zuerst. */
export function useVerwaltungsprotokoll(enabled: boolean) {
  return useQuery({
    queryKey: keys.adminLog,
    queryFn: () => api.get<Verwaltungsprotokoll>('/api/admin/log'),
    enabled,
    staleTime: 0,
  })
}

/** Ändert einen Wert der `.env`; die Antwort ist der neue Stand der ganzen Seite. */
export function useSaveEnv() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { key: string; value: string | null }) =>
      api.patch<EnvSeite>('/api/operations/env', input),
    onSuccess: (antwort) => {
      client.setQueryData(keys.envCatalogue, antwort)
      // Was die Datei ändert, ändert auch die Einstellungen: Decke, Dienste, Karte.
      void client.invalidateQueries({ queryKey: keys.settings })
    },
  })
}

export function useEnvUndo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<EnvSeite>('/api/operations/env/undo', {}),
    onSuccess: (antwort) => {
      client.setQueryData(keys.envCatalogue, antwort)
      void client.invalidateQueries({ queryKey: keys.settings })
    },
  })
}

export function useSelbstpruefung(enabled: boolean) {
  return useQuery({
    queryKey: keys.selfCheck,
    queryFn: () => api.get<Selbstpruefung>('/api/operations/check', undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    enabled,
    staleTime: 0,
  })
}

/** Was das Aufräumen der Datenbank wegnehmen würde (#82) -- die Vorschau der Karte. */
export function useDatenbankVorschau(enabled: boolean) {
  return useQuery({
    queryKey: keys.databaseCleanup,
    queryFn: () => api.get<DatenbankVorschau>('/api/operations/cleanup', undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    enabled,
    staleTime: 0,
  })
}

/** Räumt auf; danach fragt die Karte neu, und das Protokoll der Verwaltung hat eine Zeile mehr. */
export function useDatenbankAufraeumen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<DatenbankErgebnis>('/api/operations/cleanup', {}, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.databaseCleanup })
      void client.invalidateQueries({ queryKey: keys.adminLog })
    },
  })
}

/** The maintenance page as the supervisor sees it (#131). */
export function useMaintenanceState(enabled: boolean) {
  return useQuery({
    queryKey: keys.maintenance,
    queryFn: () => api.get<MaintenanceState>('/api/operations/maintenance'),
    enabled,
    staleTime: 0,
  })
}

/**
 * Switches it on or changes it. The answer carries this device's pass as a
 * cookie, so the supervisor keeps working while everyone else waits.
 */
export function useMaintenanceOn() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { scope: 'all' | 'readers'; until: string | null; text: string }) =>
      api.post<MaintenanceState>('/api/operations/maintenance', input),
    onSuccess: (answer) => {
      client.setQueryData(keys.maintenance, answer)
      void client.invalidateQueries({ queryKey: keys.adminLog })
    },
  })
}

export function useMaintenanceOff() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<MaintenanceState>('/api/operations/maintenance'),
    onSuccess: (answer) => {
      client.setQueryData(keys.maintenance, answer)
      void client.invalidateQueries({ queryKey: keys.adminLog })
    },
  })
}

/**
 * The consistency checks (#131): all, or one again. A mutation and not a
 * query -- they run on a tap, and the media check looks at every file.
 */
export function useDiagnosis() {
  return useMutation({
    mutationFn: (check?: string) =>
      api.get<DiagnosisResult>('/api/operations/diagnosis', check === undefined ? undefined : { check }, zeitgrenze(BOOK_TIMEOUT_MS)),
  })
}

export function useSetRole() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; role: Person['role'] }) =>
      api.post<{ id: number; role: Person['role'] }>(`/api/people/${input.id}/role`, {
        role: input.role,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.people })
    },
  })
}

/**
 * Vergibt einen zweiten Namen — oder nimmt ihn zurück (leer heißt löschen).
 *
 * Nur die Verwaltung sieht ihn je; das entscheidet der Server an jeder
 * Abfrage. Hier wird nur gespeichert.
 */
export function useSetPersonName() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: number; name: string }) =>
      api.post<{ id: number; admin_display_name: string | null }>(
        `/api/people/${input.id}/name`,
        { admin_display_name: input.name },
      ),
    onSuccess: () => {
      // Der Name steht nicht nur in der Personenliste: er taucht in jeder
      // Ansicht der Verwaltung auf. Also auch die Reisen und die Chatliste
      // wegwerfen — sonst stünde dort bis zum nächsten Laden der alte.
      void client.invalidateQueries({ queryKey: keys.people })
      void invalidateAll(client)
    },
  })
}

export function useCreateTrip() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      title: string
      description: string
      starts_on?: string
      ends_on?: string
    }) =>
      // The first entry of the zone course (1.87.0): the zone this device is
      // in. It has no start and covers photos from before today too.
      api.post<Trip>('/api/trips', { ...input, tz_name: zoneDesGeraets() }),
    onSuccess: () => invalidateAll(client),
  })
}

/**
 * The trip's zone course (1.87.0): add, change, remove an entry.
 *
 * Every change makes the server compute anew what took its zone from the
 * course (`TripZones::recompute`) -- points and photos move, so their lists
 * are fetched again, and the chat with them (the cards under a post carry
 * a point's time).
 */
export function useZonenverlauf(tripId: number) {
  const client = useQueryClient()
  const fertig = (antwort: { zones: Zoneneintrag[]; recomputed: number }) => {
    client.setQueryData<Trip>(keys.trip(tripId), (alt) => (alt === undefined ? alt : { ...alt, zones: antwort.zones }))
    void client.invalidateQueries({ queryKey: keys.trip(tripId) })
    if (antwort.recomputed > 0) {
      void client.invalidateQueries({ queryKey: keys.highlights(tripId) })
      void client.invalidateQueries({ queryKey: keys.reisefotos(tripId) })
      void client.invalidateQueries({ queryKey: keys.galerie(tripId) })
      void client.invalidateQueries({ queryKey: ['messages', tripId] })
    }
  }
  type Antwort = { zones: Zoneneintrag[]; recomputed: number }
  return {
    anlegen: useMutation({
      mutationFn: (input: { tz_name: string; valid_from?: string }) =>
        api.post<Antwort>(`/api/trips/${tripId}/zones`, input),
      onSuccess: fertig,
    }),
    aendern: useMutation({
      mutationFn: ({ id, ...input }: { id: number; tz_name?: string; valid_from?: string }) =>
        api.patch<Antwort>(`/api/trips/${tripId}/zones/${id}`, input),
      onSuccess: fertig,
    }),
    entfernen: useMutation({
      mutationFn: (id: number) => api.delete<Antwort>(`/api/trips/${tripId}/zones/${id}`),
      onSuccess: fertig,
    }),
  }
}

/**
 * Der Zustand einer Reise -- drei Stufen, eine Leiter.
 *
 * `active` läuft, `closed` beendet das Schreiben für alle, `private` nimmt
 * sie zusätzlich aus den Listen der Mitlesenden. Gelöscht wird in keinem
 * Fall etwas.
 */
/**
 * Titel, Beschreibung und Reisezeitraum ändern.
 *
 * Geschickt wird nur, was sich geändert hat: der Server lässt stehen, was
 * fehlt, und löscht, was leer kommt.
 */
/**
 * Was der Autor an der Karte seiner Reise ändert (#50): dieselben
 * Schlüssel wie in den Einstellungen, damit der Regler auf der Karte und die
 * Voreinstellung in der Verwaltung ein Vokabular sprechen. Leer heißt „wie
 * das Erscheinungsbild".
 */
export type KartenAenderung = Partial<Omit<MapStyle, 'route_width' | 'daycard_route_width' | 'daycard_anim_seconds' | 'daycard_anim_max' | 'daycard_anim_rate' | 'daycard_anim_pause'>> & {
  route_width?: number | ''
  daycard_route_width?: number | ''
  daycard_anim_seconds?: number | ''
  daycard_anim_max?: number | ''
  daycard_anim_rate?: number | ''
  daycard_anim_pause?: number | ''
}

export function useUpdateTrip(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (
      input: Partial<
        Pick<
          Trip,
          | 'title'
          | 'description'
          | 'starts_on'
          | 'ends_on'
          | 'mark_broadcast'
          | 'draft_chat'
          | 'playlist'
          | 'day_word'
          | 'welcome_text'
        >
      > & {
        /**
         * Die Farben der Sprechblasen dieser Reise (#49).
         *
         * Heißen hier wie in den Einstellungen und nicht wie im Ausgang
         * (`chat_bubbles`): ein Vokabular für dieselbe Sache. Leer heißt „wie
         * das Erscheinungsbild" und löscht die Angabe.
         */
        chat_own_color?: string
        chat_other_color?: string
        /** How a calculated route is asked for (1.18.0): empty clears the trip's own choice. */
        route_transport?: string
        route_avoid_tolls?: boolean | ''
      } & KartenAenderung,
    ) => api.patch<Trip>(`/api/trips/${tripId}`, input),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

export function useSetTripState(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (state: TripState) =>
      api.post<Trip>(`/api/trips/${tripId}/state`, { state }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void invalidateAll(client)
    },
  })
}

/**
 * Die aktive Reise setzen oder wieder lösen.
 *
 * **Der Server sorgt für „genau eine"** — hier wird nur gefragt. Die Liste
 * aller Reisen und die Chatliste müssen danach beide neu geholt werden: die
 * Marke wandert, und die andere Reise weiß nichts davon.
 */
export function useSetAktiv(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (aktiv: boolean) =>
      aktiv
        ? api.post<Trip>(`/api/trips/${tripId}/active`, {})
        : api.delete<Trip>(`/api/trips/${tripId}/active`),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void invalidateAll(client)
    },
  })
}

/**
 * Die Schriftgröße am Konto sichern.
 *
 * Angewendet wird sie sofort und ohne auf die Antwort zu warten — sie steht
 * schon am Wurzelelement, bevor der Server antwortet. Was hier passiert, ist
 * das Merken für die anderen Geräte.
 */
export function useSetSchrift() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (font_scale: string) => api.patch<Me>('/api/auth/me', { font_scale }),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Alles andere am Konto, was nur mit dem Aussehen zu tun hat (#54).
 *
 * Derselbe Weg wie die Schriftgröße (`PATCH /api/auth/me`) und derselbe Grund:
 * es sagt etwas über die Augen des Menschen vor dem Schirm und geht niemanden
 * sonst etwas an. Feld für Feld — was fehlt, bleibt stehen.
 */
export function useSetDarstellung() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (werte: {
      list_own_color?: string
      list_other_color?: string
      list_bg_color?: string
    }) => api.patch<Me>('/api/auth/me', werte),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Die Zahl am App-Symbol ein- oder ausschalten (0.26.0).
 *
 * Derselbe Weg (`PATCH /api/auth/me`), und aus demselben Grund am Konto:
 * der Hintergrunddienst setzt die Zahl aus dem Push, und den baut der Server
 * — ein Schalter allein im Browser hätte ihn nicht aufgehalten. Mit der
 * Antwort steht das Konto neu im Vorrat, und `useSymbolzahl()` räumt oder
 * setzt sofort.
 */
export function useSetSymbolzahl() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (app_badge: boolean) => api.patch<Me>('/api/auth/me', { app_badge }),
    onSuccess: (me) => client.setQueryData(keys.me, me),
  })
}

/**
 * Wie voll es ist — nur für die Verwaltung.
 *
 * **`enabled` und kein Rateversuch:** wer keine Verwaltung ist, bekommt ein
 * 403, und ein Fehler, der bei jedem Öffnen der Einstellungen im Protokoll
 * steht, ist Lärm. Gefragt wird also nur, wenn die Karte auch dasteht.
 *
 * Kein `staleTime`: die Zahl ändert sich mit jedem Upload, und wer sie
 * ansieht, will wissen, wie es *jetzt* aussieht.
 */
export function useSpeicher(enabled: boolean) {
  return useQuery({
    queryKey: keys.speicher,
    queryFn: () => api.get<Speicher>('/api/storage'),
    enabled,
  })
}

/**
 * Welche Dateien nicht überall abspielbar sind (#41).
 *
 * **Auf Knopfdruck und nicht beim Öffnen.** Die Prüfung sieht in jede Datei
 * hinein; das ist bei ein paar hundert Videos spürbar, und niemand braucht
 * es, nur weil er die Einstellungen aufschlägt. Deshalb `enabled: false` und
 * ein `refetch()` am Knopf — dieselbe Haltung wie bei der Umgebungssuche, die
 * auch erst auf Verlangen hinausgeht.
 */
export function useMedienpruefung() {
  return useQuery({
    queryKey: keys.mediaCheck,
    queryFn: () => api.get<Medienpruefung>('/api/storage/medien', undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    enabled: false,
    // Sie ist teuer und ändert sich nur, wenn jemand etwas hochlädt oder
    // ersetzt -- ein zweiter Lauf beim Zurückkommen wäre Arbeit ohne Auskunft.
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Dateien und Einträge gegeneinander (#20).
 *
 * **Auf Knopfdruck, wie die Kodierungsprüfung** — und aus demselben Grund:
 * der Abgleich geht das ganze Medienverzeichnis durch. Ein eigener Knopf und
 * nicht derselbe, denn es sind zwei verschiedene Kosten und zwei verschiedene
 * Fragen.
 */
/**
 * Der Abgleich, und ab wo.
 *
 * `ab` ist die Marke aus `weiter` der vorigen Antwort: der Lauf sieht sich
 * höchstens vierhundert Dateien an, und ohne die Marke wären es immer
 * dieselben vierhundert. Sie steht im Schlüssel, damit ein Weitersehen eine
 * eigene Antwort bekommt und nicht die alte überschreibt.
 */
export function useDateiabgleich(ab: string | null = null) {
  return useQuery({
    queryKey: [...keys.abgleich, ab],
    queryFn: () =>
      api.get<Dateiabgleich>(`/api/storage/abgleich${ab === null ? '' : `?ab=${encodeURIComponent(ab)}`}`, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * Die Einträge wegräumen, deren Datei fehlt.
 *
 * Danach werden **beide** Karten neu geholt: der Abgleich, weil die Liste
 * kürzer wird — und der Speicherplatz, weil dessen Zahl die Bytes dieser
 * Zeilen mitgezählt hat. Bliebe die alte stehen, sähe der Knopf aus, als
 * hätte er nichts getan.
 *
 * **Den Abgleich holt aber die Karte selbst nach, nicht dieser Haken.** Er
 * steht auf `enabled: false` und läuft nur auf Knopfdruck; eine solche Abfrage
 * gilt react-query als abgeschaltet, und **weder `invalidateQueries` noch
 * `refetchQueries` fassen sie an** — beide sieben sie vorher aus. Sie wäre nur
 * als veraltet vermerkt und stünde unverändert da, bis jemand den Knopf noch
 * einmal drückt: genau die stille Absage, die es nicht geben soll. Das
 * `refetch()` an der Abfrage selbst kennt diese Sperre nicht, und deshalb
 * steht es dort, wo die Abfrage steht.
 */
export function useFehlendeRaeumen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ files: number; bytes: number }>('/api/storage/abgleich/sweep', {}, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

/**
 * Eine einzelne herrenlose Datei wegwerfen (#20).
 *
 * **Angesprochen über ihren Namen** — eine Zeile gibt es ja nicht. Ob sie
 * wirklich herrenlos ist, entscheidet der Server im Augenblick des Aufrufs;
 * was hier hinausgeht, ist nur eine Behauptung.
 */
export function useHerrenloseLoeschen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      api.delete<{ bytes: number }>(`/api/storage/abgleich/datei/${name}`),
    onSuccess: () => {
      // Der belegte Platz ändert sich damit auch -- diese Bytes zählte die
      // Speicherkarte zwar nie, aber die Summe des Verzeichnisses schon.
      // Die Liste holt die Karte selbst nach; der Grund steht oben.
      void client.invalidateQueries({ queryKey: keys.speicher })
    },
  })
}

/**
 * Wegräumen, was an nichts hängt.
 *
 * Danach wird die Karte neu geholt — sonst stünde die alte Zahl da, und der
 * Knopf sähe aus, als hätte er nichts getan.
 */
export function useAufraeumen() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ files: number; bytes: number }>('/api/storage/sweep', {}, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.speicher }),
  })
}

export function useSetMuted(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (muted: boolean) =>
      api.post<{ muted: boolean }>(`/api/trips/${tripId}/mute`, { muted }),
    onSuccess: ({ muted }) => {
      client.setQueryData<Trip>(keys.trip(tripId), (old) =>
        old ? { ...old, muted } : old,
      )
      void client.invalidateQueries({ queryKey: keys.threads })
    },
  })
}

/**
 * Zieht einen Einladungslink zurück.
 *
 * Der Code bleibt in der Datenbank stehen (`revoked_at`), verschwindet aber
 * aus der Liste und führt nirgends mehr hin. Rückgängig macht das niemand --
 * deshalb fragt die Oberfläche vorher.
 */
export function useRevokeInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.delete<void>(`/api/trips/${tripId}/invitations/${code}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useCreateInvitation(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (maxUses?: number) =>
      api.post<Invitation>(`/api/trips/${tripId}/invitations`, { max_uses: maxUses }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

export function useAcceptInvitation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api.post<{ state: 'joined' | 'pending'; trip: Trip | null }>(
        `/api/invitations/${code}/accept`,
      ),
    // **`me` muss mit.** Dort stehen `may_use_app` und `waiting_for` — beides
    // ändert sich durch genau diesen Klick. Ohne das landete ein
    // Anklopfender auf der Wartetafel und las dort „Sobald dich jemand zu
    // einem Reisebericht einlädt, geht es hier weiter" — die Auskunft für
    // jemanden, der nichts getan hat, an den, der gerade angeklopft hat.
    onSuccess: () => Promise.all([
      invalidateAll(client),
      client.invalidateQueries({ queryKey: keys.me }),
    ]),
  })
}

/**
 * Wo man mit dieser Einladung steht.
 *
 * Gebraucht, seit zwischen dem Klick auf den Link und dem Mitlesen eine
 * Bestätigung liegen kann. Nach dem Anlegen des Kontos wüsste die
 * Einladungsseite sonst nicht, ob sie in die Reise führen oder „wartet auf
 * Bestätigung" zeigen soll.
 */
export function useInvitationStatus(code: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitationStatus(code),
    queryFn: () => api.get<InvitationStatus>(`/api/invitations/${code}/status`),
    enabled,
    retry: false,
  })
}

export function useJoinRequests(tripId: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.requests(tripId),
    queryFn: () => api.get<JoinRequest[]>(`/api/trips/${tripId}/requests`),
    enabled,
  })
}

export function useDecideRequest(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      userId: number
      approve: boolean
      disableAccount?: boolean
      /** Antwort auf die Rückfrage „der Link ist aufgebraucht" (409). */
      trotzdem?: boolean
    }) =>
      api.post<void>(`/api/trips/${tripId}/requests/${input.userId}`, {
        approve: input.approve,
        disable_account: input.disableAccount ?? false,
        trotzdem: input.trotzdem ?? false,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.requests(tripId) })
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
      void client.invalidateQueries({ queryKey: keys.people })
      void client.invalidateQueries({ queryKey: keys.trip(tripId) })
      void client.invalidateQueries({ queryKey: keys.threads })
      // Der Zähler des Links hat sich bewegt -- und die Liste blendet
      // aufgebrauchte Links aus.
      void client.invalidateQueries({ queryKey: keys.invitations(tripId) })
    },
  })
}

/**
 * Jemanden aus dem Verteiler nehmen -- oder wieder hinein.
 *
 * Der leise Bruder von `useRemoveMember`: die Reise bleibt in seiner Liste,
 * nur kommt nichts Neues mehr dazu.
 */
export function useSetCutoff(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: number; cut: boolean }) =>
      api.post<{ user_id: number; cut_off: boolean }>(
        `/api/trips/${tripId}/members/${input.userId}/cutoff`,
        { cut: input.cut },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.members(tripId) })
    },
  })
}

export function useSetApproval(tripId: number) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (required: boolean) =>
      api.post<Trip>(`/api/trips/${tripId}/approval`, { required }),
    onSuccess: (trip) => {
      client.setQueryData(keys.trip(tripId), trip)
      void client.invalidateQueries({ queryKey: keys.trips })
    },
  })
}

export function useLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Me>('/api/auth/login', input),
    onSuccess: (me) => {
      // Maybe someone who passes the maintenance (#131): the next answer decides.
      maintenanceOver()
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useRegister() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      email: string
      password: string
      display_name: string
      invite_code?: string
    }) => api.post<Me>('/api/auth/register', input),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

/**
 * Fordert einen Link zum Zurücksetzen an.
 *
 * Der Server antwortet immer gleich, auch wenn es die Adresse nicht gibt --
 * die Oberfläche darf hier also nichts unterscheiden, was er verschweigt.
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api.post<void>('/api/auth/forgot', { email }),
  })
}

export function useResetPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<Me>('/api/auth/reset', input),
    onSuccess: (me) => {
      // Das Zurücksetzen meldet gleich an; alle bisherigen Sitzungen hat der
      // Server verworfen.
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

export function useLogout() {
  const client = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => {
      // Alles verwerfen: nach dem Abmelden darf nichts aus dem Cache mehr
      // durchscheinen. Dazu gehört auch der zuletzt offene Chat -- wer sich
      // als Nächstes an diesem Gerät anmeldet, hat mit ihm nichts zu tun.
      client.clear()
      vergissOrt()
      // **Und der Vorrat auf der Platte ebenso**, sonst überlebte genau das
      // den Vorgang, was hier gerade weggeworfen wird. Der Zwischenspeicher
      // der Hülle bleibt dagegen stehen: dort liegen JavaScript, CSS und eine
      // leere Seite, nichts Persönliches -- ihn wegzuwerfen machte nur den
      // nächsten Start langsam.
      void vorratLeeren()
      // **And to the login, from every place that logs out** (1.91.0). The
      // settings page gave this jump itself and the waiting screen did not:
      // after the cache was cleared it drew itself anew with an empty
      // account -- from use, "Noch nichts freigegeben, Angemeldet als ()".
      // One rule in two places, and one of them lacked it.
      navigate('/login', { replace: true })
    },
  })
}

function invalidateAll(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.threads }),
    client.invalidateQueries({ queryKey: keys.trips }),
  ])
}

// ---- Passkeys (#21) --------------------------------------------------------

export function usePasskeys() {
  return useQuery({
    queryKey: keys.passkeys,
    queryFn: () => api.get<Passkey[]>('/api/auth/passkeys'),
  })
}

/**
 * Create a passkey: fetch the options, ask the device, hand in the response.
 *
 * Three steps in one mutation, so the card knows only one state. If the
 * person cancels at the device, `createPasskey` throws -- the card reads
 * that via `isAbort()` and says so instead of showing an error.
 */
/**
 * The browser's part comes in as a promise, started by the caller **inside
 * the gesture** (`hooks/passkeyOptions.ts` says why); here only the server's
 * part follows.
 */
export function useCreatePasskey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (job: { attestation: Promise<Registration> }) => {
      const response = await job.attestation
      return api.post<Passkey>('/api/auth/passkeys', response)
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.passkeys }),
  })
}

export function useRemovePasskey() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/auth/passkeys/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.passkeys }),
  })
}

/**
 * Sign in with the passkey -- the same consequences as `useLogin`.
 *
 * `mediation: 'conditional'` is the suggestion in the address field
 * (autofill); without it, it is the button. Both fetch a fresh challenge:
 * the old one is valid for five minutes and exactly once.
 */
/** As above: the assertion is the caller's, the login is the server's. */
export function usePasskeyLogin() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (job: { assertion: Promise<Login> }) => {
      const response = await job.assertion
      return api.post<Me>('/api/auth/passkeys/login', response)
    },
    onSuccess: (me) => {
      maintenanceOver()
      client.setQueryData(keys.me, me)
      void invalidateAll(client)
    },
  })
}

/**
 * How long the browser waits for the server to build a book. The server
 * gives itself five minutes (`BookController`); whatever takes longer is not
 * going to arrive, and a button that says "wird gebaut" for ever is a silent
 * failure -- from use (0.30.9).
 */
const BOOK_TIMEOUT_MS = 6 * 60 * 1000
/*
 * **And the same limit for everything that works on purpose for long**
 * (1.78.0): routes, re-reading a recording, the checks and sweeps of storage
 * and database. Since `request()` gives every request an end of its own
 * (`LESEN_MS`, `SCHREIBEN_MS` in the client), these would otherwise be cut
 * off while the server is still at it.
 */
/** The trip's numbers: the server raises its own limit to five minutes (`StatsController`). */
const STATS_TIMEOUT_MS = 5 * 60 * 1000 + 15_000


/**
 * Have the book built as a file (#14).
 *
 * The server answers with the address of the finished file; it is fetched
 * via a link with `download`, because an iPhone needs an address it can
 * save. The maps come along as JPEG from the browser.
 */
/** The media as a ZIP (#75): the server packs, the link comes back -- with the book's time limit. */
export function useMedienExport(tripId: number) {
  return useMutation({
    mutationFn: () =>
      api.post<{ url: string; filename: string; files: number; named: number; lookups: number; bytes: number }>(`/api/trips/${tripId}/media-export`, {}, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
  })
}

/**
 * The backup of the installation (#77): database and `.env`, encrypted with
 * the passphrase. It can take a while on a large database, so it gets the
 * book's time limit; the record of the administration gets a new line.
 */
export function useSicherung() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (passphrase: string) =>
      api.post<{ url: string; filename: string; bytes: number; tables: number; rows: number; trips: number; points: number }>(
        '/api/operations/backup',
        { passphrase },
        undefined,
        undefined,
        zeitgrenze(BOOK_TIMEOUT_MS),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.adminLog })
    },
  })
}

export function useBuildBook(tripId: number) {
  return useMutation({
    mutationFn: (job: {
      format: 'pdf' | 'epub'
      with?: number
      from: string
      to: string
      maps: { route?: string; points: Record<string, string> }
    }) => api.post<{ url: string; filename: string; bytes: number; seconds: number }>(`/api/trips/${tripId}/book`, job, undefined, undefined, zeitgrenze(BOOK_TIMEOUT_MS)),
  })
}
